/* prettier-ignore-file */













import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  Image,
  Linking,
  Platform,
  Modal,
  Animated,
  FlatList,
} from "react-native";
import { useParams, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase.config";
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { getCollectionPath } from "../utils/dbPaths";
import { useBookings, useProducts, useSettings, useCheckInStore, useAuthStore, useCompletedBookings, useCustomer, useStripeTestMode } from "../stores";
import AuthHeader from "../components/AuthHeader";
import {
  capitalizeWords,
  formatTime,
  formatPhoneNumberDashes as formatPhoneNumber,
  formatAddressLines as formatAddress,
  centsToDollars,
} from "../utils/formatting";
import { logs } from "../utils/helperFuns";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import ContactOptionsModal from "../utils/ContactOptionsModal";
import PinAuthLayer from "../components/PinAuthLayer";
import { isDeviceTrusted } from "../utils/deviceTrust";
import { readAndCompressImage } from 'browser-image-resizer';

const ViewBooking = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [contactModalVisible, setContactModalVisible] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showProcessingTooltip, setShowProcessingTooltip] = useState(false);
  
  // Refs for scrolling to sections
  const waiverContainerRef = useRef(null);
  const paymentMethodsRef = useRef(null);
  
  // Get data from stores
  const bookings = useBookings(state => state.bookings);
  const completedBookings = useCompletedBookings(state => state.completedBookings);
  const products1 = useProducts(state => state.products1);
  const products2 = useProducts(state => state.products2);
  const settings = useSettings(state => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  
  // Alias for add-ons data (products2)
  const addOnsData = products2;
  
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setCustomer = useCustomer((state) => state.setCustomer);

  // Local state for booking (with fallback to completedBookings)
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isFromCompletedBookings, setIsFromCompletedBookings] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  // Animated value for blinking banner
  const blinkOpacity = useRef(new Animated.Value(1)).current;
  // Animated value for blinking waiver button (unauthorized users only)
  const waiverBlinkOpacity = useRef(new Animated.Value(1)).current;

  // Load booking from store or completedBookings
  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      // First check Zustand store (active bookings)
      const foundInStore = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
      
      if (foundInStore) {
        setBooking(foundInStore);
        setIsFromCompletedBookings(false);
        setError(null);
        setLoading(false);
        return;
      }

      // Not in store - check if store is still loading
      // If bookings store is empty, still check completedBookings and Firestore
      // Don't wait indefinitely

      // Store is loaded but booking not found - check completedBookings Zustand store first
      const foundInCompletedStore = completedBookings.find(b => b.id === bookingId || b.bookingId === bookingId);

      if (foundInCompletedStore) {
        setBooking(foundInCompletedStore);
        setIsFromCompletedBookings(true);
        setError(null);
        setLoading(false);
        return;
      }

      // Not in stores - check Firestore directly
      // Try bookings first (public read) for unauthenticated users
      try {
        const bookingsPath = getCollectionPath("bookings");
        let bookingDoc = await getDoc(doc(db, bookingsPath, bookingId));
        
        // Check if document exists - handle both exists() method and data() check
        let docExists = bookingDoc && (typeof bookingDoc.exists === 'function' ? bookingDoc.exists() : bookingDoc.data() !== undefined);

        if (docExists) {
          const booking = { id: bookingDoc.id, ...bookingDoc.data() };
          setBooking(booking);
          setIsFromCompletedBookings(false);
          setError(null);
          setLoading(false);
          return;
        }

        // If not found in bookings, try completedBookings (requires auth)
        // This will fail for unauthenticated users, which is expected
        if (isAuthenticated) {
          const completedPath = getCollectionPath("completedBookings");
          bookingDoc = await getDoc(doc(db, completedPath, bookingId));
          docExists = bookingDoc && (typeof bookingDoc.exists === 'function' ? bookingDoc.exists() : bookingDoc.data() !== undefined);

          if (docExists) {
            const completedBooking = { id: bookingDoc.id, ...bookingDoc.data() };
            setBooking(completedBooking);
            setIsFromCompletedBookings(true);
            setError(null);
            setLoading(false);
            return;
          }
        }

        // Not found in either collection
        setIsFromCompletedBookings(false);
        setError("Booking not found");
      } catch (err) {
        // Permission errors for completedBookings are expected for unauthenticated users
        if (err.code === 'permission-denied' && !isAuthenticated) {
          setError("Booking not found");
        } else {
          setError("Error loading booking");
        }
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId, bookings, completedBookings]);

  // Blinking animation for rental return is today banner
  useEffect(() => {
    if (!booking?.endDate) {
      // Reset animation if no end date
      blinkOpacity.setValue(1);
      return;
    }

    const endDateObj = booking.endDate.toDate
      ? booking.endDate.toDate()
      : new Date(booking.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    endDateObj.setHours(0, 0, 0, 0);
    const endsToday = endDateObj.getTime() === today.getTime();

    if (endsToday) {
      // Start blinking animation: on for 0.5s, off for 0.5s
      const blinkAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(blinkOpacity, {
            toValue: 0,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(blinkOpacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      blinkAnimation.start();

      return () => {
        blinkAnimation.stop();
        blinkOpacity.setValue(1);
      };
    } else {
      // Reset to visible if not today
      blinkOpacity.setValue(1);
    }
  }, [booking?.endDate, blinkOpacity]);

  // Calculate if Sign Waiver button should be shown
  // Check waiverSigned boolean only
  const hasWaiver = booking?.waiverSigned || false;
  const showSignWaiver = !hasWaiver && !isFromCompletedBookings;

  // Blinking animation for Sign Waiver button (unauthorized users only) - red color
  useEffect(() => {
    if (!isAuthenticated && showSignWaiver) {
      // Start blinking animation: on for 0.5s, off for 0.5s
      const waiverBlinkAnimation = Animated.loop(
        Animated.sequence([
          Animated.timing(waiverBlinkOpacity, {
            toValue: 0.3,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(waiverBlinkOpacity, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
        ])
      );
      waiverBlinkAnimation.start();

      return () => {
        waiverBlinkAnimation.stop();
        waiverBlinkOpacity.setValue(1);
      };
    } else {
      // Reset to visible if authenticated or button not shown
      waiverBlinkOpacity.setValue(1);
    }
  }, [isAuthenticated, showSignWaiver, waiverBlinkOpacity]);

  // Check device trust for unauthenticated users
  useEffect(() => {
    if (isAuthenticated) {
      setIsVerified(true);
      return;
    }

    if (!bookingId) {
      return;
    }

    // Check if device is trusted
    const trustInfo = isDeviceTrusted(bookingId);
    if (trustInfo) {
      setIsVerified(true);
    } else {
      setIsVerified(false);
    }
  }, [bookingId, isAuthenticated]);


  // Derived settings
  const storeName = settings?.storeName?.name || "";
  const storeLogo = settings?.storeLogo?.url || "";
  const customerBlurb = settings?.customerBlurb?.text || settings?.customerBlurb?.blurb || "";
  const confirmationHeadingTransportation = settings?.confirmationHeadingTransportation?.heading || "";
  const confirmationHeadingInStorePickup = settings?.confirmationHeadingInStorePickup?.heading || "";
  const product1CategoryName = settings?.product1Title?.title || "Product1";
  const product2CategoryName = settings?.product2Title?.title || "Product2";
  
  // Format hours of operation object into readable string
  const formatHours = (hoursObj) => {
    if (!hoursObj || typeof hoursObj !== 'object') return null;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const formattedDays = days.map((day, index) => {
      const dayData = hoursObj[day];
      if (!dayData || !dayData.open) return null;

      const openTime = dayData.openTime || '';
      const closeTime = dayData.closeTime || '';
      return `${dayNames[index]}: ${openTime}-${closeTime}`;
    }).filter(Boolean);

    return formattedDays.length > 0 ? formattedDays.join(', ') : null;
  };

  const shopInfo = {
    phone: settings?.storePhone?.phone || "",
    email: settings?.storeEmail?.email || "",
    address: settings?.startingAddress?.address || "",
    website: settings?.storeWebsite?.url || "",
    hours: formatHours(settings?.hoursOfOperation?.hours)
  };
  
  const [transferEmail, setTransferEmail] = useState("");
  const [sendingTransfer, setSendingTransfer] = useState(false);
  const [reportingBreakdown, setReportingBreakdown] = useState(false);
  const [sendingWaiverReminder, setSendingWaiverReminder] = useState(false);
  const [showReportTypeModal, setShowReportTypeModal] = useState(false);

  // Problem report modal state
  const [showProblemDescriptionModal, setShowProblemDescriptionModal] = useState(false);
  const [problemDescription, setProblemDescription] = useState("");
  const [problemLocation, setProblemLocation] = useState("");
  const [isGPSWrong, setIsGPSWrong] = useState(false);
  const [problemPhotos, setProblemPhotos] = useState([]);
  const [problemGPS, setProblemGPS] = useState(null); // { latitude, longitude }
  const [problemGPSCity, setProblemGPSCity] = useState(null); // City/state for problemGPS
  const [isRoadsideBreakdown, setIsRoadsideBreakdown] = useState(false);
  const [submittingProblemReport, setSubmittingProblemReport] = useState(false);
  const [fetchingGPS, setFetchingGPS] = useState(false);
  const [showGPSConfirmationModal, setShowGPSConfirmationModal] = useState(false);
  const [pendingGPS, setPendingGPS] = useState(null); // GPS coordinates pending confirmation
  const [pendingGPSCity, setPendingGPSCity] = useState(null); // City/township from reverse geocoding
  const [gpsMapLoaded, setGpsMapLoaded] = useState(false);
  const [gpsConfirmed, setGpsConfirmed] = useState(false); // Track if GPS was confirmed as correct
  const gpsMapRef = useRef(null);

  // Load booking with fallback to completedBookings
  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) {
        setError("No booking ID provided");
        setLoading(false);
        return;
      }

      // First check Zustand store (active bookings)
      const foundInStore = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
      
      if (foundInStore) {
        setBooking(foundInStore);
        setIsFromCompletedBookings(false);
        setError(null);
        setLoading(false);
        return;
      }

      // Not in store - check if store is still loading
      if (bookings.length === 0) {
        return; // Still loading
      }

      // Store is loaded but booking not found - check completedBookings Zustand store first
      const foundInCompletedStore = completedBookings.find(b => b.id === bookingId || b.bookingId === bookingId);

      if (foundInCompletedStore) {
        setBooking(foundInCompletedStore);
        setIsFromCompletedBookings(true);
        setError(null);
        setLoading(false);
        return;
      }

      // Not in stores - check Firestore directly
      // Try bookings first (public read) for unauthenticated users
      try {
        const bookingsPath = getCollectionPath("bookings");
        let bookingDoc = await getDoc(doc(db, bookingsPath, bookingId));
        
        // Check if document exists - handle both exists() method and data() check
        let docExists = bookingDoc && (typeof bookingDoc.exists === 'function' ? bookingDoc.exists() : bookingDoc.data() !== undefined);

        if (docExists) {
          const booking = { id: bookingDoc.id, ...bookingDoc.data() };
          setBooking(booking);
          setIsFromCompletedBookings(false);
          setError(null);
          setLoading(false);
          return;
        }

        // If not found in bookings, try completedBookings (requires auth)
        // This will fail for unauthenticated users, which is expected
        if (isAuthenticated) {
          const completedPath = getCollectionPath("completedBookings");
          bookingDoc = await getDoc(doc(db, completedPath, bookingId));
          docExists = bookingDoc && (typeof bookingDoc.exists === 'function' ? bookingDoc.exists() : bookingDoc.data() !== undefined);

          if (docExists) {
            const completedBooking = { id: bookingDoc.id, ...bookingDoc.data() };
            setBooking(completedBooking);
            setIsFromCompletedBookings(true);
            setError(null);
            setLoading(false);
            return;
          }
        }

        // Not found in either collection
        setIsFromCompletedBookings(false);
        setError("Booking not found");
      } catch (err) {
        // Permission errors for completedBookings are expected for unauthenticated users
        if (err.code === 'permission-denied' && !isAuthenticated) {
          setError("Booking not found");
        } else {
          setError("Error loading booking");
        }
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId, bookings, completedBookings]);

  // Get check-in store setters
  const setCheckInBooking = useCheckInStore((state) => state.setBooking);
  const setBookingIntake = useCheckInStore((state) => state.setBookingIntake);
  const setProducts1Catalog = useCheckInStore((state) => state.setProducts1Catalog);
  const setProducts2Catalog = useCheckInStore((state) => state.setProducts2Catalog);
  const setCheckInShopInfo = useCheckInStore((state) => state.setShopInfo);
  const setCheckInStoreLogo = useCheckInStore((state) => state.setStoreLogo);
  const setCheckInProduct1CategoryName = useCheckInStore((state) => state.setProduct1CategoryName);
  const setCheckInProduct2CategoryName = useCheckInStore((state) => state.setProduct2CategoryName);

  // IMPORTANT: All hooks must be called before any conditional returns
  // All useMemo hooks must be called unconditionally, even if booking is null

  // Hook 1: isEndDateTodayOrBefore
  const isEndDateTodayOrBefore = useMemo(() => {
    if (!booking?.endDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let end;
    if (booking.endDate.toDate) {
      end = booking.endDate.toDate();
    } else if (booking.endDate instanceof Date) {
      end = booking.endDate;
    } else {
      end = new Date(booking.endDate);
    }
    end.setHours(0, 0, 0, 0);
    return end.getTime() <= today.getTime();
  }, [booking?.endDate]);

  // Hook 2: aggregatedItems (moved from RentalItemsSection)
    const aggregatedItems = useMemo(() => {
      if (!booking?.product1 || !booking?.product2) {
        return { aggregatedProduct1: [], aggregatedProduct2: [] };
      }
      const product1Items = booking.product1;
      const product2Items = booking.product2;

      // Aggregate product1 by name/id — skip removed or zero-quantity items
      const product1Map = new Map();
      product1Items.forEach((item) => {
        const qty = item.quantity || 0;
        if (qty <= 0 || item.removedFromOriginalBooking) return;
        const key = item.id;

        if (product1Map.has(key)) {
          const existing = product1Map.get(key);
          existing.quantity = existing.quantity + qty;
        } else {
          product1Map.set(key, {
            ...item,
            quantity: qty,
          });
        }
      });

      // Aggregate product2 (now an array)
      const product2Map = new Map();
      if (Array.isArray(product2Items)) {
        product2Items.forEach((item) => {
          const addOnId = item.id;
          const qty = item.quantity || 0;

          if (qty > 0) {
            if (product2Map.has(addOnId)) {
              const existing = product2Map.get(addOnId);
              existing.quantity = existing.quantity + qty;
            } else {
              product2Map.set(addOnId, {
                id: addOnId,
                name: item.name,
                quantity: qty,
              });
            }
          }
        });
      }

      const aggregatedProduct1 = Array.from(product1Map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      const aggregatedProduct2 = Array.from(product2Map.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      return { aggregatedProduct1, aggregatedProduct2 };
    }, [booking?.product1, booking?.product2]);

  // Hook 3: pricingData (moved from PricingSummary)
  const pricingData = useMemo(() => {
    if (!booking) {
      return {
        equipmentSubtotal: 0,
        deliveryCost: 0,
        pickupCost: 0,
        platformFee: 0,
        creditCardFee: 0,
        tax: 0,
        salesTaxPercent: 0,
        finalTotal: 0,
        totalDurationDiscount: 0,
        totalDeliveryDiscount: 0,
        totalDiscounts: 0
      };
    }

    // Use top-level fields
    const cardProcessingFeeCents = booking.cardProcessingFeeAmount || 0;
    const platformFeeCents = booking.bookingPlatformFee || 0;

    const durationDiscountCents = booking.durationDiscount?.discountCents || 0;
    const deliveryDiscountCents = booking.transportationDiscount?.discountCents || 0;

    const equipmentSubtotal = (booking.productPriceAmount || 0) / 100;
    const deliveryCost = (booking.deliveryCostCents || 0) / 100;
    const pickupCost = (booking.pickupCostCents || 0) / 100;
    const platformFee = platformFeeCents / 100;
    const creditCardFee = cardProcessingFeeCents / 100;
    const tax = (booking.salesTaxAmount || 0) / 100;
    const totalDiscounts = (durationDiscountCents + deliveryDiscountCents) / 100;

    const subtotalAfterDiscounts = equipmentSubtotal + deliveryCost + pickupCost - totalDiscounts;
    const finalTotal = subtotalAfterDiscounts + platformFee + creditCardFee + tax;

    return {
      equipmentSubtotal,
      deliveryCost,
      pickupCost,
      platformFee,
      creditCardFee,
      tax,
      salesTaxPercent: booking.salesTaxPercent,
      finalTotal,
      totalDurationDiscount: durationDiscountCents / 100,
      totalDeliveryDiscount: deliveryDiscountCents / 100,
      totalDiscounts
    };
  }, [booking?.salesTaxPercent, booking?.bookingPlatformFee, booking?.productPriceAmount, booking?.cardProcessingFeeAmount, booking?.salesTaxAmount, booking?.totalAmount, booking?.deliveryCostCents, booking?.pickupCostCents, booking?.durationDiscount, booking?.transportationDiscount]);

  // Hook 4: paymentData (moved from PaymentMethodsSection)
  const paymentData = useMemo(() => {
    const paymentList = booking?.payments || [];
    const totalPaid = paymentList.reduce(
      (sum, payment) => {
        // Convert from cents to dollars - ONLY use payment.amount
        if (payment.amount !== undefined && payment.amount !== null) {
          return sum + (payment.amount / 100);
        }
        return sum;
      },
      0
    );
    return { paymentList, totalPaid, hasPayments: paymentList.length > 0 };
  }, [booking?.payments]);

  // Check authentication status - handled by store

  // Removed data syncing effects - using direct store access

  // Helper function to process booking data
  const processBooking = (bookingData) => {
    try {
      // Booking is in the flat structure
      return bookingData;
    } catch (error) {
      return bookingData;
    }
  };

  // Component: Rental Items Display
  const RentalItemsSection = ({
    aggregatedItems,
    product1CategoryName,
    product2CategoryName,
  }) => {
    const { aggregatedProduct1, aggregatedProduct2 } = aggregatedItems;
    const hasItems =
      aggregatedProduct1.length > 0 || aggregatedProduct2.length > 0;

    if (!hasItems) {
      return null;
    }

    return (
      <View style={[styles.section, styles.product1Section]}>
        <Text style={styles.sectionTitle}>🛒 Your Items</Text>

        {aggregatedProduct1.length > 0 && (
          <View style={{ marginTop: 8 }}>
            <Text
              style={[styles.label, { fontWeight: "600", marginBottom: 8 }]}
            >
              {product1CategoryName}
            </Text>
            {aggregatedProduct1.map((item, index) => (
              <View key={index} style={styles.infoRow}>
                <Text style={styles.value}>
                  {item.quantity}x {item.name}
                </Text>
              </View>
            ))}
          </View>
        )}

        {aggregatedProduct2.length > 0 && (
          <View
            style={{
              marginTop: aggregatedProduct1.length > 0 ? 16 : 8,
            }}
          >
            <Text
              style={[styles.label, { fontWeight: "600", marginBottom: 8 }]}
            >
              {product2CategoryName}
            </Text>
            {aggregatedProduct2.map((item, index) => (
              <View key={index} style={styles.infoRow}>
                <Text style={styles.value}>
                  {item.quantity}x {item.name}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Open the delivery address in the map viewer (same behavior as the Today page cards)
  const openDeliveryAddressInMap = () => {
    if (!booking?.deliveryAddress) return;
    const capitalizedName =
      capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Unknown";
    navigate("/map-viewer", {
      state: {
        address: booking.deliveryAddress,
        customerName: booking.fullName || capitalizedName,
      },
    });
  };

  // Component: Delivery Address Display
  const DeliveryAddressDisplay = ({ deliveryAddress }) => {
    if (!deliveryAddress || deliveryAddress.trim() === "") {
      return null;
    }

    const addressParts = formatAddress(deliveryAddress);
    return (
      <View
        style={{
          marginTop: 12,
          paddingTop: 12,
          borderTopWidth: 1,
          borderTopColor: "#EDF1F0",
        }}
      >
        <Text style={[styles.label, { marginBottom: 4 }]}>
          Destination Address:
        </Text>
        <TouchableOpacity
          style={styles.addressContainer}
          onPress={openDeliveryAddressInMap}
        >
          <Text style={styles.addressLinkText}>📍 {addressParts.line1}</Text>
          {addressParts.line2 && (
            <Text style={styles.addressLinkText}>{addressParts.line2}</Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  // Component: Pricing Summary
  const PricingSummary = ({ pricingData, formatCurrency, showProcessingTooltip, setShowProcessingTooltip }) => {

    return (
      <View style={[styles.section, styles.pricingSection]}>
        <Text style={styles.sectionTitle}>💰 Pricing Summary</Text>

        {pricingData.equipmentSubtotal > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Equipment Subtotal:</Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.equipmentSubtotal)}
            </Text>
          </View>
        )}

        {pricingData.deliveryCost > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Delivery:</Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.deliveryCost)}
            </Text>
          </View>
        )}

        {pricingData.pickupCost > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Pickup:</Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.pickupCost)}
            </Text>
          </View>
        )}

        {pricingData.totalDiscounts > 0 && (
          <>
            {pricingData.totalDurationDiscount > 0 && (
              <View style={styles.priceRow}>
                <Text style={[styles.priceLabel, styles.discount]}>Duration Discount:</Text>
                <Text style={[styles.priceValue, styles.discount]}>
                  -{formatCurrency(pricingData.totalDurationDiscount)}
                </Text>
              </View>
            )}
            {pricingData.totalDeliveryDiscount > 0 && (
              <View style={styles.priceRow}>
                <Text style={[styles.priceLabel, styles.discount]}>Transportation Discount:</Text>
                <Text style={[styles.priceValue, styles.discount]}>
                  -{formatCurrency(pricingData.totalDeliveryDiscount)}
                </Text>
              </View>
            )}
            {pricingData.totalDiscounts > 0 && (
              <View style={styles.priceRow}>
                <Text style={[styles.priceLabel, styles.discount, { fontWeight: "600" }]}>Total Discounts:</Text>
                <Text style={[styles.priceValue, styles.discount, { fontWeight: "600" }]}>
                  -{formatCurrency(pricingData.totalDiscounts)}
                </Text>
              </View>
            )}
          </>
        )}

        <View style={[styles.priceRow, { marginTop: pricingData.totalDiscounts > 0 ? 8 : 0, paddingTop: pricingData.totalDiscounts > 0 ? 8 : 0, borderTopWidth: pricingData.totalDiscounts > 0 ? 1 : 0, borderTopColor: "#EDF1F0" }]}>
          <Text style={[styles.priceLabel, { fontWeight: "600" }]}>Subtotal:</Text>
          <Text style={[styles.priceValue, { fontWeight: "600" }]}>
            {formatCurrency(pricingData.equipmentSubtotal + pricingData.deliveryCost + pricingData.pickupCost - pricingData.totalDiscounts)}
          </Text>
        </View>

        {pricingData.platformFee > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Platform Fee:</Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.platformFee)}
            </Text>
          </View>
        )}
        {pricingData.creditCardFee > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>Card Processing Fee:</Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.creditCardFee)}
            </Text>
          </View>
        )}

        {pricingData.tax > 0 && (
          <View style={styles.priceRow}>
            <Text style={styles.priceLabel}>
              Sales Tax ({pricingData.salesTaxPercent}%):
            </Text>
            <Text style={styles.priceValue}>
              {formatCurrency(pricingData.tax)}
            </Text>
          </View>
        )}

        <View style={[styles.priceRow, styles.totalRow]}>
          <Text style={styles.totalLabel}>Total:</Text>
          <Text style={styles.totalValue}>
            {formatCurrency(pricingData.finalTotal)}
          </Text>
        </View>
      </View>
    );
  };

  // Component: Payment Methods Display
  const PaymentMethodsSection = ({ paymentData, booking, bookingId, settings, navigate, isAuthenticated, isFromCompletedBookings }) => {
    const [collectingPayment, setCollectingPayment] = useState(false);
    const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
    const [payingInvoice, setPayingInvoice] = useState(false);

    const handleCollectNow = async () => {
      if (!booking || !bookingId) {
        alert("Error: Booking information not available");
        return;
      }

      const totalAmountCents = booking.totalAmount || 0;
      if (totalAmountCents <= 0) {
        alert("Error: Invalid payment amount");
        return;
      }

      try {
        setCollectingPayment(true);

        // Dynamically import functions
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const { app } = await import("../firebase.config");
        const functions = getFunctions(app);

        // Create payment link
        const createPaymentLink = httpsCallable(functions, "createPaymentLink");
        const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";

        const paymentResult = await createPaymentLink({
          amountCents: totalAmountCents,
          description: `Payment for Booking ${bookingId}`,
          customerEmail: booking.customerEmail || "",
          customerPhone: booking.customerPhone || "",
          customerName: customerName,
          saveCard: true,
          bookingId: bookingId,
          isBookingEdit: true, // Mark as edit booking so receipts are sent
          skipBookingConfirmation: true, // Skip booking confirmation, only send receipts
          testMode: useTestStripe,
        });

        if (!paymentResult.data?.url) {
          throw new Error("Failed to create payment link");
        }

        // Navigate to Stripe Checkout
        navigate("/stripe-checkout", {
          state: {
            paymentUrl: paymentResult.data.url,
            bookingId: bookingId,
            referrer: `/view-booking/${bookingId}`, // Store where we came from
          },
        });
        // Don't reset - modal stays visible during navigation
      } catch (error) {
        console.error("❌ Error creating payment link:", error);
        setCollectingPayment(false);
        alert("Error: " + (error.message || "Failed to create payment link"));
      }
    };

    const handleSendPaymentLink = async () => {
      if (!booking || !bookingId) {
        alert("Error: Booking information not available");
        return;
      }

      const totalAmountCents = booking.totalAmount || 0;
      if (totalAmountCents <= 0) {
        alert("Error: Invalid payment amount");
        return;
      }

      if (!booking.customerPhone) {
        alert("Error: Customer phone number not available");
        return;
      }

      try {
        setSendingPaymentLink(true);

        // Dynamically import functions
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const { app } = await import("../firebase.config");
        const functions = getFunctions(app);

        // Create payment link first
        const createPaymentLink = httpsCallable(functions, "createPaymentLink");
        const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";

        const paymentResult = await createPaymentLink({
          amountCents: totalAmountCents,
          description: `Payment for Booking ${bookingId}`,
          customerEmail: booking.customerEmail || "",
          customerPhone: booking.customerPhone || "",
          customerName: customerName,
          saveCard: true,
          bookingId: bookingId,
          isBookingEdit: true, // Mark as edit booking so receipts are sent
          skipBookingConfirmation: true, // Skip booking confirmation, only send receipts
          testMode: useTestStripe,
        });

        if (!paymentResult.data?.url) {
          throw new Error("Failed to create payment link");
        }

        // Send SMS with payment link
        const sendPaymentLinkSms = httpsCallable(functions, "sendPaymentLinkSms");
        await sendPaymentLinkSms({
          bookingId: bookingId,
          paymentUrl: paymentResult.data.url,
          customerPhone: booking.customerPhone,
          customerName: customerName,
        });

        alert("Payment link sent successfully!");
      } catch (error) {
        console.error("❌ Error sending payment link:", error);
        alert("Error: " + (error.message || "Failed to send payment link"));
      } finally {
        setSendingPaymentLink(false);
      }
    };

    const handlePayInvoice = async () => {
      if (!booking || !bookingId) {
        alert("Error: Booking information not available");
        return;
      }

      const totalAmountCents = booking.totalAmount || 0;
      if (totalAmountCents <= 0) {
        alert("Error: Invalid payment amount");
        return;
      }

      try {
        setPayingInvoice(true);

        // Dynamically import functions
        const { getFunctions, httpsCallable } = await import("firebase/functions");
        const { app } = await import("../firebase.config");
        const functions = getFunctions(app);

        // Create payment link
        const createPaymentLink = httpsCallable(functions, "createPaymentLink");
        const customerName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";

        const paymentResult = await createPaymentLink({
          amountCents: totalAmountCents,
          description: `Payment for Booking ${bookingId}`,
          customerEmail: booking.customerEmail || "",
          customerPhone: booking.customerPhone || "",
          customerName: customerName,
          saveCard: true,
          bookingId: bookingId,
          isBookingEdit: true, // Mark as edit booking so receipts are sent
          skipBookingConfirmation: true, // Skip booking confirmation, only send receipts
          testMode: useTestStripe,
        });

        if (!paymentResult.data?.url) {
          throw new Error("Failed to create payment link");
        }

        // Navigate to Stripe Checkout
        navigate("/stripe-checkout", {
          state: {
            paymentUrl: paymentResult.data.url,
            bookingId: bookingId,
            referrer: `/view-booking/${bookingId}`, // Store where we came from
          },
        });
      } catch (error) {
        console.error("❌ Error creating payment link:", error);
        alert("Error: " + (error.message || "Failed to create payment link"));
        setPayingInvoice(false);
      }
    };

    const refunds = booking?.refunds && Array.isArray(booking.refunds) ? booking.refunds : [];
    // All amounts stored in cents (same as payments)
    const refundToDollars = (r) => Number(r.amount ?? r.refundAmount ?? 0) / 100;
    const totalRefunded = refunds.reduce((sum, r) => sum + refundToDollars(r), 0);

    const formatShortDate = (ts) => {
      if (!ts) return "—";
      const d = ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>💳 Payments</Text>
        {paymentData.hasPayments ? (
          <>
            {paymentData.paymentList.map((payment, index) => {
              const paymentType = payment.type === "initial" ? "Initial Booking" : payment.type;
              const cardInfo = payment.cardInfo;
              const brand = cardInfo?.brand || "";
              const last4 = cardInfo?.last4 || "";
              const expMonth = cardInfo?.expMonth || "";
              const expYear = cardInfo?.expYear || "";
              const expiration = expMonth && expYear ? `${expMonth}/${expYear}` : "";
              const paymentDate = formatShortDate(payment.timestamp);
              return (
                <View key={`payment-${index}`} style={styles.infoRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.label, { color: "#1F8A5B" }]}>
                      Payment {index + 1}{paymentType ? ` (${paymentType})` : ""}
                    </Text>
                    <Text style={[styles.value, { fontSize: 12, color: "#1F8A5B" }]}>
                      {paymentDate}
                    </Text>
                    {cardInfo && (brand || last4 || expiration) && (
                      <Text style={[styles.value, { fontSize: 12, color: "#5C6B68" }]}>
                        {brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : ""}
                        {last4 ? ` •••• ${last4}` : ""}
                        {expiration ? ` • ${expiration}` : ""}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.value, { color: "#1F8A5B", fontWeight: "500" }]}>
                    {formatCurrency((payment.amount || 0) / 100)}
                  </Text>
                </View>
              );
            })}
            {refunds.map((refund, index) => {
              const refundDollars = refundToDollars(refund);
              const pct = refund.refundPercent ?? refund.refundPercentage ?? 0;
              const refundDate = formatShortDate(refund.timestamp ?? refund.createdAt);
              return (
                <View key={`refund-${index}`} style={styles.infoRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.label, { color: "#C4453D" }]}>
                      Refund {index + 1}{pct ? ` (${pct}%)` : ""}
                    </Text>
                    <Text style={[styles.value, { fontSize: 12, color: "#C4453D" }]}>
                      {refundDate}
                    </Text>
                    {(refund.refundReason || refund.stripeRefundId) && (
                      <Text style={[styles.value, { fontSize: 12, color: "#8A9794" }]}>
                        {refund.refundReason || (refund.stripeRefundId ? `Stripe ID: ${refund.stripeRefundId}` : "")}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.value, { color: "#C4453D", fontWeight: "500" }]}>
                    -{formatCurrency(refundDollars)}
                  </Text>
                </View>
              );
            })}
            <View
              style={[
                styles.infoRow,
                {
                  marginTop: 12,
                  paddingTop: 12,
                  borderTopWidth: 1,
                  borderTopColor: "#EDF1F0",
                },
              ]}
            >
              <Text style={[styles.label, { fontWeight: "bold" }]}>
                Total Paid:
              </Text>
              <Text style={[styles.value, { fontWeight: "bold" }]}>
                ${paymentData.totalPaid.toFixed(2)}
              </Text>
            </View>
            {totalRefunded > 0 && (
              <View style={styles.infoRow}>
                <Text style={[styles.label, { fontWeight: "bold", color: "#5C6B68" }]}>
                  Total Refunded:
                </Text>
                <Text style={[styles.value, { fontWeight: "bold", color: "#C4453D" }]}>
                  -${totalRefunded.toFixed(2)}
                </Text>
              </View>
            )}
          </>
        ) : (
            <>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Manual Booking, no payment collected</Text>
              </View>
            </>
        )}
        {/* Refunds as line items when there are no payments but refunds exist */}
        {!paymentData.hasPayments && refunds.length > 0 && (
          <>
            {refunds.map((refund, index) => {
              const refundDollars = refundToDollars(refund);
              const pct = refund.refundPercent ?? refund.refundPercentage ?? 0;
              const refundDate = formatShortDate(refund.timestamp ?? refund.createdAt);
              return (
                <View key={`refund-${index}`} style={styles.infoRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.label, { color: "#C4453D" }]}>
                      Refund {index + 1}{pct ? ` (${pct}%)` : ""}
                    </Text>
                    <Text style={[styles.value, { fontSize: 12, color: "#C4453D" }]}>
                      {refundDate}
                    </Text>
                    {(refund.refundReason || refund.stripeRefundId) && (
                      <Text style={[styles.value, { fontSize: 12, color: "#8A9794" }]}>
                        {refund.refundReason || (refund.stripeRefundId ? `Stripe ID: ${refund.stripeRefundId}` : "")}
                      </Text>
                    )}
                  </View>
                  <Text style={[styles.value, { color: "#C4453D", fontWeight: "500" }]}>
                    -{formatCurrency(refundDollars)}
                  </Text>
                </View>
              );
            })}
            <View style={[styles.infoRow, { marginTop: 8 }]}>
              <Text style={[styles.label, { fontWeight: "bold", color: "#5C6B68" }]}>Total Refunded:</Text>
              <Text style={[styles.value, { fontWeight: "bold", color: "#C4453D" }]}>-${totalRefunded.toFixed(2)}</Text>
            </View>
          </>
        )}
        {!paymentData.hasPayments && (
            <>
              {/* Hide payment buttons if booking is from completedBookings */}
              {!isFromCompletedBookings && (
                <>
                  {isAuthenticated ? (
                    // Authenticated users see both buttons
                    <View style={styles.paymentButtonsContainer}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[
                          styles.collectButton,
                          (collectingPayment || sendingPaymentLink || payingInvoice) && styles.collectButtonDisabled
                        ]}
                        onPress={handleCollectNow}
                        disabled={collectingPayment || sendingPaymentLink || payingInvoice}
                      >
                        {collectingPayment ? (
                          <ActivityIndicator color="#FFFFFF" size="small" />
                        ) : (
                          <Text style={styles.collectButtonText}>💳 Collect Payment</Text>
                        )}
                      </TouchableOpacity>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[
                          styles.sendLinkButton,
                          (collectingPayment || sendingPaymentLink || payingInvoice) && styles.sendLinkButtonDisabled
                        ]}
                        onPress={handleSendPaymentLink}
                        disabled={collectingPayment || sendingPaymentLink || payingInvoice}
                      >
                        {sendingPaymentLink ? (
                          <ActivityIndicator color="#FFFFFF" size="small" />
                        ) : (
                          <Text style={styles.sendLinkButtonText}>📱 Send Text Invoice</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  ) : (
                    // Unauthenticated users see single "Pay Invoice" button
                    <View style={styles.paymentButtonsContainer}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[
                          styles.payInvoiceButton,
                          payingInvoice && styles.payInvoiceButtonDisabled
                        ]}
                        onPress={handlePayInvoice}
                        disabled={payingInvoice}
                      >
                        {payingInvoice ? (
                          <ActivityIndicator color="#FFFFFF" size="small" />
                        ) : (
                          <Text style={styles.payInvoiceButtonText}>💳 Pay Invoice</Text>
                        )}
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              )}
            </>
        )}
        
        {/* Loading Modal for Collect Payment */}
        <Modal
          visible={collectingPayment}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Creating payment link...</Text>
            </View>
          </View>
        </Modal>

        {/* Loading Modal for Send Invoice */}
        <Modal
          visible={sendingPaymentLink}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Sending invoice...</Text>
            </View>
          </View>
        </Modal>

        {/* Loading Modal for Pay Invoice */}
        <Modal
          visible={payingInvoice}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Processing payment...</Text>
            </View>
          </View>
        </Modal>
      </View>
    );
  };


  const formatDate = (date) => {
    if (!date) return "N/A";

    let dateObj;
    if (date.toDate) {
      dateObj = date.toDate();
    } else if (date instanceof Date) {
      dateObj = date;
    } else {
      dateObj = new Date(date);
    }

    return dateObj.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (typeof amount === "number") {
      return `$${amount.toFixed(2)}`;
    }
    return "$0.00";
  };

  const handleEditBooking = () => {
    // The wizard, not the old /edit-booking page. Safe here: the button that
    // calls this is inside an isAuthenticated block, and /edit-booking-wizard is
    // deliberately NOT in App.js's publicPaths.
    const bookingIdToEdit = booking.bookingId;
    navigate(`/edit-booking-wizard/${bookingIdToEdit}`);
  };

  const handleSendWaiverTransfer = async () => {
    if (!transferEmail || !transferEmail.includes("@")) {
      alert("Please enter a valid email address");
      return;
    }

    try {
      setSendingTransfer(true);

      // Import Firebase functions
      const { getFunctions, httpsCallable } = await import(
        "firebase/functions"
      );
      const { app } = await import("../firebase.config");
      const functions = getFunctions(app);

      // Call the cloud function
      const sendWaiverTransferEmail = httpsCallable(
        functions,
        "sendWaiverTransferEmail"
      );
      const result = await sendWaiverTransferEmail({
        bookingId: booking.bookingId,
        transferToEmail: transferEmail,
        customerFirstName: booking.firstName,
        customerLastName: booking.lastName,
      });

      alert(
        `Waiver request sent to ${transferEmail}. They will receive an email with instructions.`
      );
      setTransferEmail(""); // Clear the input
    } catch (error) {
      console.error("❌ Error sending waiver transfer:", error);
      alert(
        "Failed to send waiver request. Please try again or contact the shop."
      );
    } finally {
      setSendingTransfer(false);
    }
  };

  // Capture photo from camera
  // Delete a photo from the problem report
  const deleteProblemPhoto = (index) => {
    setProblemPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Take photo using inline camera for problem report
  const takeProblemPhoto = async () => {
    try {
      const imageBase64 = await capturePhoto();
      // Add photo to the problem photos array
      setProblemPhotos(prev => [...prev, imageBase64]);
    } catch (error) {
      console.error("❌ Error capturing photo:", error);
      if (error.message && !error.message.includes("cancelled")) {
        alert("Failed to capture photo: " + error.message);
      }
    }
  };

  const capturePhoto = async () => {
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Camera access not supported by your browser");
    }

    console.log("📸 Requesting camera access...");

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "environment", // Prefer back camera on mobile
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    });

    // Create video element to display camera stream
    const video = document.createElement("video");
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.style.position = "fixed";
    video.style.top = "0";
    video.style.left = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "10000";
    video.style.backgroundColor = "#0F1A19";

    // Create capture button
    const captureButton = document.createElement("button");
    captureButton.textContent = "📸 Capture Photo";
    captureButton.style.position = "fixed";
    captureButton.style.bottom = "40px";
    captureButton.style.left = "50%";
    captureButton.style.transform = "translateX(-50%)";
    captureButton.style.zIndex = "10001";
    captureButton.style.padding = "16px 32px";
    captureButton.style.fontSize = "18px";
    captureButton.style.fontWeight = "bold";
    captureButton.style.backgroundColor = "#C4453D";
    captureButton.style.color = "#FFFFFF";
    captureButton.style.border = "none";
    captureButton.style.borderRadius = "8px";
    captureButton.style.cursor = "pointer";
    captureButton.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";

    // Create cancel button
    const cancelButton = document.createElement("button");
    cancelButton.textContent = "✕ Cancel";
    cancelButton.style.position = "fixed";
    cancelButton.style.top = "20px";
    cancelButton.style.right = "20px";
    cancelButton.style.zIndex = "10001";
    cancelButton.style.padding = "12px 24px";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.fontWeight = "bold";
    cancelButton.style.backgroundColor = "rgba(0,0,0,0.7)";
    cancelButton.style.color = "#FFFFFF";
    cancelButton.style.border = "none";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.cursor = "pointer";

    document.body.appendChild(video);
    document.body.appendChild(captureButton);
    document.body.appendChild(cancelButton);

    // Wait for video to be ready
    await new Promise((resolve) => {
      video.onloadedmetadata = () => {
        video.play();
        resolve();
      };
    });

    // Capture photo when button is clicked
    const imageBase64 = await new Promise((resolve, reject) => {
      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(video);
        document.body.removeChild(captureButton);
        document.body.removeChild(cancelButton);
      };

      captureButton.onclick = () => {
        // Create canvas to capture frame
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0);

        // Convert to base64
        const base64 = canvas.toDataURL("image/jpeg", 0.9);
        cleanup();
        resolve(base64);
      };

      cancelButton.onclick = () => {
        cleanup();
        reject(new Error("Camera capture cancelled"));
      };
    });

    console.log("📸 Image captured, size:", imageBase64.length);
    return imageBase64;
  };

  // Get GPS location
  const getGPSLocation = async () => {
    if (!navigator.geolocation) {
      throw new Error("Geolocation not supported by your browser");
    }

    // Request GPS permission and get location
    console.log("📍 Requesting GPS location permission...");
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        (error) => {
          console.error("❌ Geolocation error:", error);
          if (error.code === error.PERMISSION_DENIED) {
            reject(new Error("Location permission denied. Please enable location access in your browser settings and try again."));
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            reject(new Error("Location information unavailable. Please check your GPS settings."));
          } else if (error.code === error.TIMEOUT) {
            reject(new Error("Location request timed out. Please try again."));
          } else {
            reject(new Error("Failed to get location: " + error.message));
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        }
      );
    });

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    // Don't log coordinates - user doesn't want to see them
    return { latitude, longitude };
  };

  // Submit report to cloud function
  const submitReport = async (photos, description, location, latitude, longitude, roadsideBreakdown) => {
    if (!photos || photos.length === 0) {
      alert("Please take at least one photo before submitting.");
      return;
    }

    if (!description || description.trim() === "") {
      alert("Please describe the problem before submitting.");
      return;
    }

    try {
      setSubmittingProblemReport(true);

      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const { app } = await import("../firebase.config");
      const functions = getFunctions(app);

      const reportBreakdownFunction = httpsCallable(functions, "reportBreakdown");

      // Get tenant/store context - use booking data as fallback for unauthorized users
      let tenantId = getTenantId();
      let storeId = getStoreId();
      
      // Fallback: if tenantId/storeId are not available (unauthorized users), try to get from booking data
      if ((!tenantId || !storeId) && booking) {
        tenantId = tenantId || booking.tenantId || null;
        storeId = storeId || booking.storeId || null;
      }

      // Process and compress photos (same as Photo.js implementation)
      const imageBase64Array = await Promise.all(
        photos.map(async (photo) => {
          try {
            let imageData;
            
            // Handle different photo formats
            if (typeof photo === 'string') {
              // Already a data URI string
              if (photo.startsWith('data:')) {
                const response = await fetch(photo);
                imageData = await response.blob();
              } else {
                // Assume it's already base64, return as-is (shouldn't happen but handle it)
                return photo;
              }
            } else if (photo.uri) {
              // Has a URI property
              const response = await fetch(photo.uri);
              imageData = await response.blob();
            } else if (photo.data) {
              // Has a data property (from Photo page)
              if (typeof photo.data === 'string' && photo.data.startsWith('data:')) {
                const response = await fetch(photo.data);
                imageData = await response.blob();
              } else {
                imageData = photo.data;
              }
            } else {
              // Try to use photo directly as blob
              imageData = photo;
            }

            // Compress image (same settings as Photo.js)
            const resizedBlob = await readAndCompressImage(imageData, {
              quality: 0.8,
              maxWidth: 1920,
              maxHeight: 1920,
            });

            // Convert compressed blob to base64
            const base64String = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(resizedBlob);
            });

            return base64String;
          } catch (error) {
            console.error('❌ Failed to process photo for problem report:', error);
            // Fallback: try to return original photo as string
            if (typeof photo === 'string') {
              return photo;
            }
            if (photo.uri) {
              return photo.uri;
            }
            throw error;
          }
        })
      );

      // Submit first photo as imageBase64 for backward compatibility, and include all photos
      const result = await reportBreakdownFunction({
        bookingId: bookingId,
        imageBase64: imageBase64Array[0], // First photo for backward compatibility
        imagesBase64: imageBase64Array, // All photos
        description: description.trim(),
        location: location?.trim() || null,
        latitude: latitude,
        longitude: longitude,
        tenantId: tenantId,
        storeId: storeId,
        roadsideBreakdown: roadsideBreakdown,
      });

      console.log("✅ Problem reported successfully:", result.data);

      // Reset form
      setProblemDescription("");
      setProblemLocation("");
      setProblemPhotos([]);
      setProblemGPS(null);
      setProblemGPSCity(null);
      setShowProblemDescriptionModal(false);

      alert(
        "✅ Problem reported successfully!\n\n" +
        `• ${photos.length} photo(s) uploaded\n` +
        (latitude && longitude ? "• Location saved\n" : "") +
        "• SMS sent to delivery phone\n" +
        "• Email sent to store"
      );
    } catch (error) {
      console.error("❌ Error submitting problem report:", error);
      alert("Failed to submit problem report: " + (error.message || "Please try again."));
    } finally {
      setSubmittingProblemReport(false);
    }
  };

  // Handle submitting the problem report from modal
  const handleSubmitProblemReport = async () => {
    await submitReport(
      problemPhotos,
      problemDescription,
      problemLocation,
      problemGPS?.latitude || null,
      problemGPS?.longitude || null,
      isRoadsideBreakdown
    );
  };

  // Reverse geocode coordinates to get city/township with state
  const reverseGeocode = async (latitude, longitude) => {
    try {
      const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        // Extract city/township and state from address components
        const result = data.results[0];
        const addressComponents = result.address_components || [];

        // Look for city, town, or locality and state
        let city = null;
        let state = null;

        for (const component of addressComponents) {
          // Get city/township
          if (!city && component.types.includes("locality")) {
            city = component.long_name;
          } else if (!city && component.types.includes("administrative_area_level_2")) {
            // County level fallback
            city = component.long_name;
          } else if (!city && component.types.includes("administrative_area_level_3")) {
            // Sub-county level fallback
            city = component.long_name;
          }

          // Get state abbreviation
          if (!state && component.types.includes("administrative_area_level_1")) {
            state = component.short_name; // Use short_name for state abbreviation (e.g., "FL")
          }
        }

        // Fallback to formatted address if no city found
        if (!city && result.formatted_address) {
          // Try to extract city from formatted address
          const parts = result.formatted_address.split(",");
          if (parts.length > 1) {
            city = parts[parts.length - 2].trim(); // Usually city is second to last
          }
        }

        // Format: "City, ST" or just "City" if no state
        if (city && state) {
          return `${city}, ${state}`;
        } else if (city) {
          return city;
        } else if (result.formatted_address) {
          return result.formatted_address;
        }

        return "Unknown location";
      }

      return "Unknown location";
    } catch (error) {
      console.error("❌ Error reverse geocoding:", error);
      return "Unknown location";
    }
  };

  // Handle roadside breakdown (with GPS)
  const handleRoadsideBreakdown = async () => {
    if (!booking || !bookingId) {
      alert("Error: Booking data not available");
      return;
    }

    try {
      setShowReportTypeModal(false);
      setFetchingGPS(true); // Show blocking modal
      
      // Get GPS location
      const { latitude, longitude } = await getGPSLocation();
      
      // Reverse geocode to get city/township
      const city = await reverseGeocode(latitude, longitude);

      // Hide blocking modal and show GPS confirmation modal
      setFetchingGPS(false);
      setPendingGPS({ latitude, longitude });
      setPendingGPSCity(city);
      setShowGPSConfirmationModal(true);
    } catch (error) {
      console.error("❌ Error getting GPS location:", error);
      
      let errorMessage = "Failed to get location. ";
      if (error.message.includes("Geolocation") || error.message.includes("Location")) {
        errorMessage += error.message;
      } else {
        errorMessage += error.message || "Please try again.";
      }

      setFetchingGPS(false);
      alert(errorMessage);
      setShowReportTypeModal(false);
    }
  };

  // Handle GPS confirmation - user says coordinates are correct
  const handleGPSCorrect = async () => {
    if (pendingGPS) {
      setProblemGPS(pendingGPS);
      // Reverse geocode to get city/state for display
      const city = await reverseGeocode(pendingGPS.latitude, pendingGPS.longitude);
      setProblemGPSCity(city);
      setIsRoadsideBreakdown(true);
      setGpsConfirmed(true); // Mark GPS as confirmed
      setShowGPSConfirmationModal(false);
      setPendingGPS(null);
      setPendingGPSCity(null);
      setGpsMapLoaded(false);
      setIsGPSWrong(false); // GPS is correct - location not required
      setShowProblemDescriptionModal(true);
    }
  };

  // Handle GPS rejection - user says coordinates are wrong
  const handleGPSWrong = () => {
    setPendingGPS(null);
    setPendingGPSCity(null);
    setGpsConfirmed(false); // GPS was not confirmed
    setShowGPSConfirmationModal(false);
    setGpsMapLoaded(false);
    // Still allow them to proceed without GPS
    setIsRoadsideBreakdown(true);
    setIsGPSWrong(true); // Mark GPS as wrong - location will be required
    setShowProblemDescriptionModal(true);
  };

  // Initialize Google Maps for GPS confirmation modal
  useEffect(() => {
    if (!showGPSConfirmationModal || !pendingGPS) return;

    const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";

    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      initializeGPSMap();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com"]`
    );
    if (existingScript) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          initializeGPSMap();
          clearInterval(checkLoaded);
        }
      }, 100);
      setTimeout(() => clearInterval(checkLoaded), 10000);
      return;
    }

    // Load Google Maps script
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google && window.google.maps) {
        initializeGPSMap();
      }
    };
    document.head.appendChild(script);
  }, [showGPSConfirmationModal, pendingGPS]);

  // Initialize the map in GPS confirmation modal
  const initializeGPSMap = () => {
    if (!window.google || !window.google.maps || !gpsMapRef.current || !pendingGPS) return;

    const map = new window.google.maps.Map(gpsMapRef.current, {
      center: { lat: pendingGPS.latitude, lng: pendingGPS.longitude },
      zoom: 15,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    // Add marker for the GPS location
    new window.google.maps.Marker({
      position: { lat: pendingGPS.latitude, lng: pendingGPS.longitude },
      map: map,
      title: "Your Location",
      icon: {
        url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
      },
    });

    setGpsMapLoaded(true);
  };

  // Handle problem report (without GPS)
  const handleProblemReport = async () => {
    if (!booking || !bookingId) {
      alert("Error: Booking data not available");
      return;
    }

    setShowReportTypeModal(false);
    setIsRoadsideBreakdown(false);
    setProblemGPS(null);
    setProblemGPSCity(null);
    setIsGPSWrong(false); // Reset GPS wrong state for new problem report

    // Show problem description modal
    setShowProblemDescriptionModal(true);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <View style={styles.loadingContent}>
          {/* Store Logo (if available) */}
          {storeLogo && (
            <Image
              source={{ uri: storeLogo }}
              style={styles.loadingLogo}
              resizeMode="center"
            />
          )}

          {/* Bike Icon */}
          <View style={styles.loadingIconContainer}>
            <Text style={styles.loadingIcon}>🚴</Text>
          </View>

          {/* Loading Text */}
          <Text style={styles.loadingTitle}>Bonita Bikes</Text>
          <Text style={styles.loadingSubtitle}>Loading booking...</Text>

          {/* Loading Bar */}
          <View style={styles.loadingBarContainer}>
            <View style={styles.loadingBar}>
              <View style={styles.loadingBarFill} />
            </View>
          </View>

          <Text style={styles.loadingHint}>Please wait</Text>
        </View>
      </View>
    );
  }

  if (error || !booking) {
    return (
      <View style={styles.container}>
        <Text style={styles.errorText}>❌ {error || "Booking not found"}</Text>
        <Text style={styles.errorSubtext}>
          Please check your booking link or contact us:
        </Text>

        {shopInfo && (
          <View
            style={{
              marginTop: 20,
              padding: 20,
              backgroundColor: "#F4F6F6",
              borderRadius: 6,
            }}
          >
            {shopInfo.phone && (
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 12,
                }}
                onPress={() => Linking.openURL(`tel:${shopInfo.phone}`)}
              >
                <Text style={{ fontSize: 16, marginRight: 10 }}>📞</Text>
                <Text
                  style={{
                    fontSize: 16,
                    color: "#0E7C6B",
                    textDecorationLine: "underline",
                  }}
                >
                  {shopInfo.phone}
                </Text>
              </TouchableOpacity>
            )}

            {shopInfo.email && (
              <TouchableOpacity
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  marginBottom: 12,
                }}
                onPress={() => Linking.openURL(`mailto:${shopInfo.email}`)}
              >
                <Text style={{ fontSize: 16, marginRight: 10 }}>✉️</Text>
                <Text
                  style={{
                    fontSize: 16,
                    color: "#0E7C6B",
                    textDecorationLine: "underline",
                  }}
                >
                  {shopInfo.email}
                </Text>
              </TouchableOpacity>
            )}

            {shopInfo.address && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <Text style={{ fontSize: 16, marginRight: 10 }}>📍</Text>
                <Text style={{ fontSize: 16, color: "#2E3A38", flex: 1 }}>
                  {shopInfo.address}
                </Text>
              </View>
            )}

            {shopInfo.hours && (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "flex-start",
                  marginBottom: 0,
                }}
              >
                <Text style={{ fontSize: 16, marginRight: 10 }}>🕒</Text>
                <Text style={{ fontSize: 16, color: "#2E3A38", flex: 1 }}>
                  {shopInfo.hours}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    );
  }

  // Now safe to access booking properties (but still need null checks)
  const startDate = booking?.startDate ? formatDate(booking.startDate) : "N/A";
  const endDate = booking?.endDate ? formatDate(booking.endDate) : "N/A";

  // Calculate days from startDate and endDate
  let days = 1;
  if (booking?.startDate && booking?.endDate) {
    const start = booking.startDate.toDate
      ? booking.startDate.toDate()
      : new Date(booking.startDate);
    const end = booking.endDate.toDate
      ? booking.endDate.toDate()
      : new Date(booking.endDate);
    days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;
  }

  // Check if pickup is today or rental ends today
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let pickupToday = false;
  let endsToday = false;

  if (booking?.startDate) {
    const startDateObj = booking.startDate.toDate
      ? booking.startDate.toDate()
      : new Date(booking.startDate);
    startDateObj.setHours(0, 0, 0, 0);
    pickupToday = startDateObj.getTime() === today.getTime();
  }

  if (booking?.endDate) {
    const endDateObj = booking.endDate.toDate
      ? booking.endDate.toDate()
      : new Date(booking.endDate);
    endDateObj.setHours(0, 0, 0, 0);
    endsToday = endDateObj.getTime() === today.getTime();
  }

  // Determine status display
  const getStatusDisplay = () => {
    if (!booking) {
      return {
        text: "Loading...",
        backgroundColor: "#5C6B68",
        textColor: "#FFFFFF",
      };
    }
    if (booking.status === "confirmed") {
      return {
        text: "Confirmed",
        backgroundColor: "#1F8A5B", // Green background
        textColor: "#FFFFFF",
      };
    } else if (booking.status === "pending_payment") {
      return {
        text: "Pending Payment",
        backgroundColor: "#B4761B",
        textColor: "#2E3A38",
      };
    } else if (booking.status === "awaiting_cash") {
      // Waiver signed, held at the register until staff confirm cash received.
      // Without this branch the raw status string would be shown.
      return {
        text: "Awaiting Cash Payment",
        backgroundColor: "#B4761B",
        textColor: "#FFFFFF",
      };
    } else if (booking.status === "cancelled") {
      return {
        text: "Cancelled",
        backgroundColor: "#C4453D",
        textColor: "#FFFFFF",
      };
    }
    return {
      text: booking.status || "Unknown",
      backgroundColor: "#5C6B68",
      textColor: "#FFFFFF",
    };
  };

  const statusDisplay = getStatusDisplay();

  // Determine which confirmation heading to show
  // Check if this is in-store pickup (no delivery and no home pickup needed)
  const isInStorePickup =
    booking?.inStorePickup === true ||
    (!booking?.delivery && !booking?.homePickup);
  const confirmationHeading = isInStorePickup
    ? confirmationHeadingInStorePickup
    : confirmationHeadingTransportation;

  // Check if check-in button should be shown
  const hasBookingIntake = booking?.bookingIntake && (
    Object.keys(booking.bookingIntake.product1 || {}).length > 0 ||
    Object.keys(booking.bookingIntake.product2 || {}).length > 0
  );

  const showCheckInButton = isAuthenticated ? isEndDateTodayOrBefore : hasBookingIntake;

  // Has the equipment physically left? There is no boolean for this -- check-out
  // is recorded in ONE of two ways and both have to count:
  //   1. the outbound photo set (Photo.js:1069 arrayUnions into media.outbound)
  //   2. an admin override, which takes the photos path but writes nothing to
  //      media.outbound -- the editedBy entry is the only trace it left
  //      (Photo.js:1574-1596)
  // Checking only the photos would leave an overridden check-out looking like it
  // never happened, which is exactly backwards for the buttons below.
  const outboundPhotoCount = booking?.media?.outbound?.length || 0;
  const hasCheckOutOverride = (booking?.editedBy || []).some(
    (entry) => entry?.changeType === "check_out_override"
  );
  const hasCheckedOut = outboundPhotoCount > 0 || hasCheckOutOverride;

  // Handle navigation to check-in page
  const handleGoToCheckIn = () => {
    if (!booking || !bookingId) return;
    
    // Ensure check-in store is populated
    setCheckInBooking(booking);
    
    // Set catalogs (filtered by retail price)
    if (products1 && products1.length > 0) {
      setProducts1Catalog(products1.filter(p => p.hasRetailPrice));
    }
    if (products2 && products2.length > 0) {
      setProducts2Catalog(products2.filter(p => p.hasRetailPrice));
    }

    if (booking.bookingIntake) {
      setBookingIntake(booking.bookingIntake);
    } else {
      setBookingIntake({
        product1: {},
        product2: {},
      });
    }
    
    navigate(`/check-in/${bookingId}`);
  };

  // Handle reactivating a booking from completedBookings
  const handleReactivateBooking = async () => {
    if (!booking || !bookingId) return;

    if (!window.confirm("Are you sure you want to reactivate this booking? This will move it back to active bookings.")) {
      return;
    }

    try {
      const tenantId = getTenantId();
      const storeId = getStoreId();

      // Get paths for both collections
      const completedBookingsPath = getCollectionPath("completedBookings");
      const bookingsPath = getCollectionPath("bookings");

      // Get the booking data from completedBookings
      const completedBookingRef = doc(db, completedBookingsPath, bookingId);
      const completedBookingSnap = await getDoc(completedBookingRef);

      if (!completedBookingSnap.exists()) {
        throw new Error("Booking not found in completedBookings");
      }

      const bookingData = completedBookingSnap.data();

      // Prepare reactivated booking data
      const reactivatedBookingData = {
        ...bookingData,
        cancelled: false,
        updatedAt: serverTimestamp(),
      };

      // Remove cancelledAt timestamp if it exists
      if (reactivatedBookingData.cancelledAt) {
        delete reactivatedBookingData.cancelledAt;
      }

      // Move booking to active bookings collection
      const activeBookingRef = doc(db, bookingsPath, bookingId);
      await setDoc(activeBookingRef, reactivatedBookingData);

      // Delete from completedBookings collection
      await deleteDoc(completedBookingRef);

      // Update local state
      setBooking(reactivatedBookingData);
      setIsFromCompletedBookings(false);

      alert("✅ Booking reactivated successfully! The booking has been moved back to active bookings.");

      // Optionally refresh the page or navigate
      window.location.reload();
    } catch (error) {
      console.error("❌ Error reactivating booking:", error);
      alert(`Error: Failed to reactivate booking - ${error.message}`);
    }
  };

  // logs(booking)

  // Show verification layer for unauthenticated users
  if (!isAuthenticated && !isVerified && bookingId) {
    return (
      <View style={styles.outerContainer}>
        <AuthHeader
          title="View Booking"
          subtitle={shopInfo?.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />
        <PinAuthLayer
          onAuthenticated={() => {
            setIsVerified(true);
          }}
          bookingId={bookingId}
        />
      </View>
    );
  }

  try {
    return (
      <View style={styles.outerContainer}>
        <AuthHeader
          title="View Booking"
          subtitle={shopInfo?.name ? `by ${shopInfo.name}` : ""}
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={shopInfo}
          storeLogo={storeLogo}
        />

        <ScrollView style={styles.container}>
          <View style={styles.content}>
            {/* Logo Banner */}
            {storeLogo && (
              <View style={styles.logoBanner}>
                <Image
                  source={{ uri: storeLogo }}
                  style={styles.bannerLogo}
                  resizeMode="contain"
                />
                {confirmationHeading && (
                  <Text style={styles.logoBannerText}>
                    {confirmationHeading}
                  </Text>
                )}
              </View>
            )}
            
            {/* Completed Booking Banner and Reactivate Button */}
            {isFromCompletedBookings && (
              <View style={styles.completedBookingContainer}>
                {booking?.cancelled ? (
                  <View style={styles.bannerRed}>
                    <Text style={styles.bannerText}>
                      Booking Cancelled
                    </Text>
                  </View>
                ) : (
                  <View style={styles.bannerGreen}>
                    <Text style={styles.bannerText}>
                      Booking Complete
                    </Text>
                  </View>
                )}
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.reactivateButton}
                  onPress={handleReactivateBooking}
                >
                  <Text style={styles.reactivateButtonText}>
                    Reactivate Booking
                  </Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Breakdown/Problem Reported Button - Top (if incomplete reports exist) */}
            {(() => {
              const breakdownReports = booking?.breakdownReports || [];
              const incompleteReports = breakdownReports.filter(report => !report.completed);

              if (incompleteReports.length > 0) {
                // Check if any incomplete report is a roadside breakdown
                const hasRoadsideBreakdown = incompleteReports.some(report => report.roadsideBreakdown === true);

                // If no roadside breakdowns, check if any report doesn't have the flag (problem report)
                const hasProblemReport = incompleteReports.some(report => report.roadsideBreakdown !== true);

                // Determine button label
                let buttonLabel = "Breakdown Reported!";
                if (hasProblemReport && !hasRoadsideBreakdown) {
                  buttonLabel = "Problem Reported!";
                } else if (hasRoadsideBreakdown && !hasProblemReport) {
                  buttonLabel = "Breakdown Reported!";
                } else if (hasRoadsideBreakdown && hasProblemReport) {
                  // If both types exist, show "Breakdown Reported!" (more urgent)
                  buttonLabel = "Breakdown Reported!";
                }

                return (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.breakdownReportedButton}
                    onPress={() => navigate(`/problem-reports/${bookingId}`)}
                  >
                    <Text style={styles.breakdownReportedButtonText}>
                      {buttonLabel}
                    </Text>
                  </TouchableOpacity>
                );
              }
              return null;
            })()}

            {/* Flagged for Review Indicator */}
            {booking?.bookingIntake?.flaggedForReview && (
              <View style={styles.flaggedIndicator}>
                <Text style={styles.flaggedIcon}>🚩</Text>
                <Text style={styles.flaggedText}>Check-in Flagged</Text>
              </View>
            )}

            {/* Rental Status Banners */}
            {pickupToday && (
              <View style={styles.bannerOrange}>
                <Text style={styles.bannerText}>
                  {booking?.delivery
                    ? `🚚 Delivery is TODAY! (${startDate})`
                    : `🚴 Rental Pickup is TODAY! (${startDate})`
                  }
                </Text>
              </View>
            )}
            {endsToday && !pickupToday && (
              <Animated.View style={[styles.bannerRed, { opacity: blinkOpacity }]}>
                <Text style={styles.bannerText}>
                  {booking?.homePickup
                    ? `🚚 Home pickup is TODAY! (${endDate})`
                    : `⏰ Rental Return is TODAY! (${endDate})`
                  }
                </Text>
              </Animated.View>
            )}
            
            {/* Action Buttons Container - Check-In, Sign Waiver, Pay Invoice */}
            {(() => {
              // Hide action buttons if booking is from completedBookings
              if (isFromCompletedBookings) return null;

              const customerName = capitalizeWords(`${booking?.firstName || ""} ${booking?.lastName || ""}`.trim());
              // Check if waiver exists - check waiverSigned boolean only
              const hasWaiver = booking?.waiverSigned || false;
              const hasPayments = booking?.payments && booking.payments.length > 0;

              const showSignWaiver = !hasWaiver;
              const showPayInvoice = !hasPayments;

              // Only show container if at least one button should be displayed
              if (!showCheckInButton && !showSignWaiver && !showPayInvoice) return null;

              const handleScrollToWaiver = () => {
                if (waiverContainerRef.current) {
                  waiverContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              };

              const handleScrollToPayment = () => {
                if (paymentMethodsRef.current) {
                  paymentMethodsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              };

              return (
                <View style={styles.actionButtonsContainer}>
                  {/* Check-In Button */}
                  {showCheckInButton && (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.checkInButton}
                      onPress={handleGoToCheckIn}
                    >
                      <Text style={styles.checkInButtonText}>
                        {hasBookingIntake ? "View Check-In" : "Start Check-In"}
                      </Text>
                    </TouchableOpacity>
                  )}

                  {/* Sign Waiver and Pay Invoice Buttons */}
                  {(showSignWaiver || showPayInvoice) && (
                    <View style={[styles.paymentButtonsContainer, { gap: 10, marginTop: showCheckInButton ? 12 : 0 }]}>
                      {showSignWaiver && (
                        <View style={[styles.waiverActionsContainer, { flex: 1, marginRight: showPayInvoice ? 5 : 0 }]}>
                          <Text style={styles.waiverRequiredText}>Customer must sign waiver before equipment transfer</Text>
                          <Animated.View style={{ opacity: !isAuthenticated ? waiverBlinkOpacity : 1 }}>
                            <TouchableOpacity dataSet={{ hover: "lift" }}
                              style={[
                                styles.signWaiverButton,
                                {
                                  backgroundColor: !isAuthenticated ? "#C4453D" : "#B4761B",
                                }
                              ]}
                              onPress={() => {
                                if (isAuthenticated) {
                                  handleScrollToWaiver();
                                } else {
                                  const hasBicycles = (booking?.product1?.length || 0) > 0;
                                  navigate("/waiver", {
                                    state: {
                                      customerName: customerName,
                                      hasBicycles: hasBicycles,
                                      bookingId: bookingId,
                                      isOriginalBooker: true,
                                    },
                                  });
                                }
                              }}
                            >
                              <Text style={[styles.signWaiverButtonText, { color: !isAuthenticated ? "#FFFFFF" : "#0F1A19" }]}>📝 Sign Waiver</Text>
                            </TouchableOpacity>
                          </Animated.View>
                        </View>
                  )}

                  {showPayInvoice && (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={[
                        styles.payInvoiceButton,
                        {
                          backgroundColor: "#C4453D",
                          flex: 1,
                          marginLeft: showSignWaiver ? 5 : 0
                        }
                      ]}
                      onPress={() => {
                        // Scroll to payment methods section centered on screen
                        handleScrollToPayment();
                      }}
                    >
                      <Text style={[styles.payInvoiceButtonText, { color: "#FFFFFF" }]}>💳 Pay Invoice</Text>
                    </TouchableOpacity>
                  )}
                    </View>
                  )}
                </View>
              );
            })()}
            
            {/* Check Out (photo) and Check In - authorized users only.
                A completed check-out mutes the button and swaps in a green
                check, but it stays pressable: staff can reopen the check-out
                screen to shoot more outbound photos. Check In only unlocks
                after the rental has gone out. */}
            {isAuthenticated && (
              <View style={styles.photoButtonRow}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[styles.checkOutButton, hasCheckedOut && styles.checkOutButtonDone]}
                  onPress={() => {
                    const fullName = capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()) || "Customer";
                    const customerObj = {
                      id: booking.customerPhone,
                      firstName: booking.firstName,
                      lastName: booking.lastName,
                      phone: booking.customerPhone,
                      fullName,
                      photoCount: 0,
                      createdAt: new Date(),
                      bookingId: booking.id || booking.bookingId,
                    };
                    setCustomer(customerObj);
                    navigate("/photo", {
                      state: {
                        category: "outbound",
                        bookingId: booking.id || booking.bookingId,
                        fromToday: false,
                      },
                    });
                  }}
                >
                  <Text style={[styles.checkOutButtonIcon, hasCheckedOut && styles.checkOutButtonIconDone]}>
                    {hasCheckedOut ? "✓" : "📸"}
                  </Text>
                  <Text style={styles.checkOutButtonText}>
                    {hasCheckedOut ? "Checked Out" : "Check Out"}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={hasCheckedOut ? { hover: "lift" } : undefined}
                  style={[styles.checkInRowButton, !hasCheckedOut && styles.flowButtonDisabled]}
                  disabled={!hasCheckedOut}
                  onPress={() => {
                    const bookingIdForCheckIn = booking.id || booking.bookingId;
                    navigate(`/check-in/${bookingIdForCheckIn}`);
                  }}
                >
                  <Text style={styles.checkInRowButtonIcon}>✓</Text>
                  <Text style={styles.checkInRowButtonText}>Check In</Text>
                </TouchableOpacity>
              </View>
            )}

            {/* Admin notes - above Booking Details */}
            {booking.adminNotes?.trim() && (
              <View style={styles.adminNotesCard}>
                <Text style={styles.adminNotesLabel}>Admin  Notes:</Text>
                <Text style={styles.adminNotesText}>{booking.adminNotes.trim()}</Text>
              </View>
            )}

            {/* Report Problem and View Reports Buttons - above Booking Details */}
            <View style={styles.reportButtonsContainer}>
              <Text style={styles.reportButtonsLabel}>
                Report a roadside breakdown or issue with your equipment here
              </Text>
              <View style={styles.reportButtonsRow}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.reportBreakdownButton}
                  onPress={() => setShowReportTypeModal(true)}
                  disabled={reportingBreakdown}
                >
                  {reportingBreakdown ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.reportBreakdownButtonText}>
                      Report Problem
                    </Text>
                  )}
                </TouchableOpacity>
                {(() => {
                  const breakdownReports = booking?.breakdownReports || [];
                  const totalCount = breakdownReports.length;
                  const completedCount = breakdownReports.filter(r => r.completed === true).length;
                  const incompleteCount = breakdownReports.filter(r => !r.completed).length;
                  const hasBothTypes = completedCount > 0 && incompleteCount > 0;
                  
                  return (
                    <TouchableOpacity dataSet={{ hover: "lift" }}
                      style={styles.viewProblemReportsButton}
                      onPress={() => navigate(`/problem-reports/${bookingId}`)}
                    >
                      <Text style={styles.viewProblemReportsButtonText}>
                        Filed Reports
                        {totalCount > 0 ? (
                          <>
                            {hasBothTypes && <Text> ({totalCount})</Text>}
                            {completedCount > 0 && (
                              <Text style={{ color: '#1F8A5B' }}> ({completedCount})</Text>
                            )}
                            {incompleteCount > 0 && (
                              <Text style={{ color: '#C4453D' }}> ({incompleteCount})</Text>
                            )}
                          </>
                        ) : (
                          <Text style={{ color: '#5C6B68' }}> (0)</Text>
                        )}
                      </Text>
                    </TouchableOpacity>
                  );
                })()}
              </View>
            </View>

            {/* Booking Details Container */}
            <View style={styles.bookingDetailsContainer}>
              <Text style={[styles.title, { color: "#0E7C6B" }]}>📋 Booking Details</Text>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Booking ID:</Text>
                <Text style={[styles.value, { fontSize: 14, fontWeight: "bold", color: "#0E7C6B" }]}>{booking.customerBookingID}</Text>
              </View>
              {/* Rental Dates - Moved from Rental Period */}
              <View style={styles.infoRow}>
                <Text style={styles.label}>Start Date:</Text>
                <Text style={[styles.value, { color: "#1F8A5B" }]}>{startDate}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>End Date:</Text>
                <Text style={[styles.value, { color: "#C4453D" }]}>{endDate}</Text>
              </View>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Duration:</Text>
                <Text style={styles.value}>
                  {days} day{days !== 1 ? "s" : ""}
                </Text>
              </View>
              {booking.delivery && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Delivery:</Text>
                  <Text style={[styles.value, { color: "#1F8A5B", fontWeight: "600" }]}>✓</Text>
                </View>
              )}
              {booking.homePickup && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Home Pickup:</Text>
                  <Text style={[styles.value, { color: "#1F8A5B", fontWeight: "600" }]}>✓</Text>
                </View>
              )}
              {/* Horizontal Divider */}
              {isAuthenticated && (
                <View style={styles.bookingDetailsDivider} />
              )}
              {/* Lock Assignment Section - Only for authenticated users */}
              {isAuthenticated && (
                <View style={styles.lockAssignmentContainer}>
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.lockAssignmentButton}
                    onPress={() => {
                      const rentalObject = {
                        id: booking.id || booking.bookingId,
                        fullName: capitalizeWords(`${booking.firstName || ""} ${booking.lastName || ""}`.trim()),
                        firstName: booking.firstName,
                        lastName: booking.lastName,
                        phone: booking.customerPhone || booking.phone,
                        assignedLocks: booking.assignedLocks || [],
                      };
                      navigate("/customer-locks", {
                        state: { rental: rentalObject }
                      });
                    }}
                  >
                    <Text style={styles.lockAssignmentButtonText}>🔒</Text>
                  </TouchableOpacity>

                  {/* Lock List */}
                  <View style={styles.lockListContainer}>
                    {(!booking.assignedLocks || booking.assignedLocks.length === 0) ? (
                      <Text style={styles.noLocksText}>No locks assigned</Text>
                    ) : (
                        booking.assignedLocks
                          .filter(lock => lock && typeof lock === 'object')
                          .sort((a, b) => {
                            const aTag = a.tagNumber || 0;
                            const bTag = b.tagNumber || 0;
                            return aTag - bTag;
                          })
                          .map((lock, index) => {
                            const tagNumber = lock.tagNumber;
                            const lockName = lock.name || `#${tagNumber}`;
                            const combination = lock.combination;

                            return (
                              <View key={index} style={styles.lockListItem}>
                                <Text style={styles.lockListText}>
                                  🏷️ {lockName} {combination ? `🔢 ${combination}` : ""}
                                </Text>
                              </View>
                            );
                          })
                    )}
                  </View>
                </View>
              )}
            </View>
            {/* Customer Information */}
            <View style={[styles.section, styles.customerSection]}>
              <Text style={styles.sectionTitle}>👤 Customer Information</Text>
              <View style={styles.infoRow}>
                <Text style={styles.label}>Name:</Text>
                <Text style={styles.value}>
                  {capitalizeWords(booking.firstName)}{" "}
                  {capitalizeWords(booking.lastName)}
                </Text>
              </View>
              {booking.customerEmail && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Email:</Text>
                  {isAuthenticated ? (
                    <TouchableOpacity
                      onPress={() => navigate("/gmail-inbox", {
                        state: {
                          customerEmail: booking.customerEmail,
                          composeTo: booking.customerEmail,
                          composeSubject: `${shopInfo?.name || "Bonita Bikes"} rental communication`,
                          customerName: capitalizeWords(`${booking.firstName} ${booking.lastName}`.trim()),
                          storeName: shopInfo?.name || "Bonita Bikes"
                        }
                      })}
                    >
                      <Text style={[styles.value, { color: "#0E7C6B", textDecorationLine: "underline" }]}>
                        {booking.customerEmail}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={styles.value}>{booking.customerEmail}</Text>
                  )}
                </View>
              )}
              {booking.customerPhone && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Phone:</Text>
                  {isAuthenticated ? (
                    <TouchableOpacity
                      onPress={() => {
                        const firstName = booking.firstName ? booking.firstName.charAt(0).toUpperCase() + booking.firstName.slice(1).toLowerCase() : '';
                        const lastName = booking.lastName ? booking.lastName.charAt(0).toUpperCase() + booking.lastName.slice(1).toLowerCase() : '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
                        if (!isMobile) {
                          navigate('/messages', {
                            state: {
                              customer: {
                                id: booking.customerPhone,
                                phone: booking.customerPhone,
                                fullName: fullName || 'Customer',
                                firstName: booking.firstName || '',
                                lastName: booking.lastName || '',
                                email: booking.customerEmail || '',
                                bookingId: bookingId,
                              }
                            }
                          });
                        } else {
                          setSelectedCustomer({
                            phone: booking.customerPhone,
                            fullName: fullName || 'Customer',
                            firstName: booking.firstName,
                            lastName: booking.lastName,
                            email: booking.customerEmail,
                            bookingId: bookingId
                          });
                          setContactModalVisible(true);
                        }
                      }}
                    >
                      <Text style={[styles.value, { color: "#0E7C6B" }]}>
                        {formatPhoneNumber(booking.customerPhone)}
                      </Text>
                    </TouchableOpacity>
                  ) : /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768 ? (
                    <TouchableOpacity
                      onPress={() => {
                        const cleanPhone = booking.customerPhone.replace(/\D/g, "");
                        if (cleanPhone.length === 10) {
                          window.location.href = `tel:+1${cleanPhone}`;
                        }
                      }}
                    >
                      <Text style={[styles.value, { color: "#0E7C6B", textDecorationLine: "underline" }]}>
                        {formatPhoneNumber(booking.customerPhone)}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={styles.value}>
                      {formatPhoneNumber(booking.customerPhone)}
                    </Text>
                  )}
                </View>
              )}
              {booking.deliveryAddress && booking.deliveryAddress.trim() !== "" && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Delivery Address:</Text>
                  <TouchableOpacity
                    style={{ flex: 1, alignItems: "flex-end" }}
                    onPress={openDeliveryAddressInMap}
                  >
                    {(() => {
                      const addressString = booking.deliveryAddress.trim();
                      // Split address by comma to create lines
                      const addressParts = addressString.split(',').map(part => part.trim());
                      const line1 = addressParts[0] || addressString;
                      const line2 = addressParts.slice(1).join(', ');
                      return (
                        <>
                          <Text style={styles.addressLinkText}>📍 {line1}</Text>
                          {line2 && (
                            <Text style={styles.addressLinkText}>{line2}</Text>
                          )}
                        </>
                      );
                    })()}
                  </TouchableOpacity>
                </View>
              )}
              {booking.gatedCommunity && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Gated Community:</Text>
                  <Text style={styles.value}>Yes</Text>
                </View>
              )}
              {booking.addressNotes && (
                <View style={styles.infoRow}>
                  <Text style={styles.label}>Address Notes:</Text>
                  <Text style={styles.value}>{booking.addressNotes}</Text>
                </View>
              )}
            </View>
            {/* Rental Items */}
            <RentalItemsSection
              aggregatedItems={aggregatedItems}
              product1CategoryName={product1CategoryName}
              product2CategoryName={product2CategoryName}
            />
            {/* Delivery Options */}
            {(booking.delivery ||
              booking.homePickup ||
              booking.inStorePickup) && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>🚚 Delivery Options</Text>
                {booking.delivery && (
                  <Text style={styles.value}>✓ Delivery Needed</Text>
                )}
                {booking.homePickup && (
                  <Text style={styles.value}>✓ Home Pickup Needed</Text>
                )}
                {/* Only show in-store pickup when neither delivery nor home pickup is active */}
                {booking.inStorePickup && !booking.delivery && !booking.homePickup && (
                  <Text style={styles.value}>✓ In-Store Pickup</Text>
                )}
                {(booking.delivery || booking.homePickup) && (
                  <DeliveryAddressDisplay
                    deliveryAddress={booking.deliveryAddress}
                  />
                )}
              </View>
            )}
            {/* Pricing */}
            <PricingSummary 
              pricingData={pricingData} 
              formatCurrency={formatCurrency}
              showProcessingTooltip={showProcessingTooltip}
              setShowProcessingTooltip={setShowProcessingTooltip}
            />
            
            {/* Tooltip overlay */}
            {showProcessingTooltip && (
              <TouchableOpacity
                style={styles.tooltipOverlay}
                activeOpacity={1}
                onPress={() => setShowProcessingTooltip(false)}
              />
            )}

            {/* Payments */}
            <div ref={paymentMethodsRef}>
              <PaymentMethodsSection
                paymentData={paymentData}
                booking={booking}
                bookingId={bookingId}
                settings={settings}
                navigate={navigate}
                isAuthenticated={isAuthenticated}
                isFromCompletedBookings={isFromCompletedBookings}
              />
            </div>

            {/* Waiver Actions - Show if waiver not signed AND booking is not from completedBookings */}
            {(() => {
              // Hide waiver section if booking is from completedBookings
              if (isFromCompletedBookings) return null;

              // Check if waiver is signed - check waiverSigned boolean only
              const hasWaiver = booking?.waiverSigned || false;
              if (hasWaiver) return null;

              return (
                <div ref={waiverContainerRef}>
                  <View style={styles.waiverActionsContainer}>
                    {/* Sign Waiver Button - Always show if no original booker waiver */}
                    <Text style={styles.waiverRequiredText}>Customer must sign waiver before equipment transfer</Text>
                    <Animated.View style={{ opacity: !isAuthenticated ? waiverBlinkOpacity : 1 }}>
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={[
                          styles.signWaiverButton,
                          {
                            backgroundColor: !isAuthenticated ? "#C4453D" : "#B4761B",
                          }
                        ]}
                        onPress={() => {
                          const hasBicycles = (booking?.product1?.length || 0) > 0;
                          const customerName = capitalizeWords(`${booking?.firstName || ""} ${booking?.lastName || ""}`.trim());

                          navigate("/waiver", {
                            state: {
                              customerName: customerName,
                              hasBicycles: hasBicycles,
                              bookingId: bookingId,
                              isOriginalBooker: true,
                            },
                          });
                        }}
                      >
                        <Text style={[styles.signWaiverButtonText, { color: !isAuthenticated ? "#FFFFFF" : "#0F1A19" }]}>📝 Sign Waiver</Text>
                      </TouchableOpacity>
                    </Animated.View>

                    {/* Send Waiver Reminder Button - Only show if authenticated */}
                    {isAuthenticated && (
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        style={styles.sendWaiverReminderButton}
                        onPress={async () => {
                          try {
                            setSendingWaiverReminder(true);

                            const { getFunctions, httpsCallable } = await import("firebase/functions");
                            const { app } = await import("../firebase.config");
                            const functions = getFunctions(app);

                            const sendWaiverReminder = httpsCallable(functions, "sendWaiverReminder");
                            await sendWaiverReminder({
                              bookingId: bookingId,
                            });

                            alert("✅ Waiver reminder sent successfully!\n\nThe customer will receive an email and SMS reminder to sign the waiver.");
                          } catch (error) {
                            console.error("❌ Error sending waiver reminder:", error);
                            alert("Error: Failed to send waiver reminder - " + error.message);
                          } finally {
                            setSendingWaiverReminder(false);
                          }
                        }}
                        disabled={sendingWaiverReminder}
                      >
                        {sendingWaiverReminder ? (
                          <ActivityIndicator color="#2E3A38" size="small" />
                        ) : (
                          <Text style={styles.sendWaiverReminderButtonText}>📧 Send Waiver Reminder</Text>
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                </div>
              );
            })()}

            {/* Customer Blurb - Only show for authenticated users */}
            {customerBlurb && isAuthenticated && (
              <View style={styles.blurbContainer}>
                <Text style={styles.blurbText}>{customerBlurb}</Text>
                {!booking.cancelled && (
                  <Text style={styles.blurbText}>
                    {"\n"}Add items, remove items, or request refunds on the edit page
                  </Text>
                )}
                {!booking.cancelled && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.editButton}
                    onPress={handleEditBooking}
                  >
                    <Text style={styles.editButtonText}>
                      ✏️ Edit Booking
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}

            {/* Contact Instructions - Show for unauthenticated users instead of Edit Booking */}
            {!booking.cancelled && !isAuthenticated && (
              <View style={styles.contactInstructionsContainer}>
                <Text style={styles.contactInstructionsTitle}>
                  To change your booking, please contact us:
                </Text>
                {shopInfo.email && (
                  <TouchableOpacity
                    style={styles.contactItem}
                    onPress={() => {
                      if (isAuthenticated) {
                        // Navigate to Gmail inbox with pre-filled email and subject
                        navigate('/gmail-inbox', {
                          state: {
                            composeTo: shopInfo.email,
                            composeSubject: 'Regarding your rental booking'
                          }
                        });
                      } else {
                        // Open native email app for unauthenticated users
                        Linking.openURL(`mailto:${shopInfo.email}?subject=Regarding your rental booking`);
                      }
                    }}
                  >
                    <Text style={styles.contactIcon}>✉️</Text>
                    <Text style={[styles.contactText, styles.contactLink]}>{shopInfo.email}</Text>
                  </TouchableOpacity>
                )}
                {shopInfo.phone && (
                  (() => {
                    const isMobile = window.innerWidth < 768;
                    const cleanPhone = shopInfo.phone.replace(/\D/g, "");
                    const canCall = isMobile && cleanPhone.length === 10;
                    
                    if (canCall) {
                      return (
                        <TouchableOpacity
                          style={styles.contactItem}
                          onPress={() => {
                            Linking.openURL(`tel:+1${cleanPhone}`);
                          }}
                        >
                          <Text style={styles.contactIcon}>📞</Text>
                          <Text style={[styles.contactText, styles.contactLink]}>
                            {formatPhoneNumber(shopInfo.phone)}
                          </Text>
                        </TouchableOpacity>
                      );
                    } else {
                      return (
                        <View style={styles.contactItem}>
                          <Text style={styles.contactIcon}>📞</Text>
                          <Text style={styles.contactText}>
                            {formatPhoneNumber(shopInfo.phone)}
                          </Text>
                        </View>
                      );
                    }
                  })()
                )}
                    {shopInfo.hours && (() => {
                      const hoursObj = settings?.hoursOfOperation?.hours;
                      if (!hoursObj || typeof hoursObj !== 'object') return null;
                      
                      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                      const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
                      
                      return (
                        <View style={styles.hoursContainer}>
                          <View style={styles.contactItem}>
                            <Text style={styles.contactIcon}>🕒</Text>
                            <Text style={styles.contactInstructionsTitle}>Store Hours:</Text>
                          </View>
                          <View style={styles.hoursList}>
                            {days.map((day, index) => {
                              const dayData = hoursObj[day];
                              if (!dayData || !dayData.open) return null;
                              
                              const openTime = dayData.openTime || '';
                              const closeTime = dayData.closeTime || '';
                              
                              // Convert 24-hour format to 12-hour format
                              const formatTo12Hour = (time24) => {
                                if (!time24) return '';
                                const [hours, minutes] = time24.split(':');
                                const hour = parseInt(hours, 10);
                                const ampm = hour >= 12 ? 'PM' : 'AM';
                                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                return `${hour12}:${minutes || '00'} ${ampm}`;
                              };
                              
                              return (
                                <View key={day} style={styles.hoursRow}>
                                  <Text style={styles.dayName}>{dayNames[index]}:</Text>
                                  <Text style={styles.hoursTime}>{formatTo12Hour(openTime)} - {formatTo12Hour(closeTime)}</Text>
                                </View>
                              );
                            })}
                          </View>
                        </View>
                      );
                    })()}
              </View>
            )}
            {/* === CUSTOMER ACTIONS SECTION === */}
            {/* Liability Transfer Section - Hide if cancelled */}
            {!booking.cancelled && (
              <View style={styles.transferSection}>
                <Text style={styles.transferTitle}>
                  Transfer Liability to Another Person
                </Text>
                <Text style={styles.transferDescription}>
                  If you choose, you may send a waiver request to another person
                  in your group to transfer liability for themselves and others
                  when they use the products you rent under your current
                  liability agreement.
                </Text>
                <TextInput
                  style={styles.transferInput}
                  placeholder="Enter their email address"
                  placeholderTextColor="#8A9794"
                  value={transferEmail}
                  onChangeText={setTransferEmail}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.transferButton}
                  onPress={handleSendWaiverTransfer}
                  disabled={sendingTransfer || !transferEmail}
                >
                  {sendingTransfer ? (
                    <ActivityIndicator color="#FFFFFF" />
                  ) : (
                    <Text style={styles.transferButtonText}>
                      Send Waiver Request
                    </Text>
                  )}
                </TouchableOpacity>
                
                {/* View Signed Waivers Button - Inside same section */}
                {((booking.waiverData && booking.waiverData.length > 0) || (booking.waivers && booking.waivers.length > 0)) && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={[styles.transferButton, { marginTop: 12 }]}
                    onPress={() => {
                      // Use waiverData if available, fallback to waivers for legacy
                      const waiversToShow = booking.waiverData || booking.waivers || [];
                      navigate(`/view-waivers/${bookingId}`, {
                        state: { waivers: waiversToShow, bookingId },
                      });
                    }}
                  >
                    <Text style={styles.transferButtonText}>
                      📝 View Signed Waivers ({booking.waiverData?.length || booking.waivers?.length || 0})
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            )}
            {/* Photos & Videos Section */}
            <View style={styles.photosVideosContainer}>
              <Text style={styles.photosVideosLabel}>
                View equipment check-out, check-in and other media for your booking
              </Text>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.photosVideosButton}
                onPress={() => navigate(`/all-media/${bookingId}`)}
              >
                <Text style={styles.photosVideosButtonText}>
                  📷 Photos & Videos
                </Text>
              </TouchableOpacity>
            </View>
            {/* Cancellation Policy & Cancel Button - Hide if already cancelled */}
            {/* Store Website Link */}
            {shopInfo?.website && (
              <Text style={styles.websiteText}>
                🌐 For more information, visit:{" "}
                <Text
                  style={styles.websiteLink}
                  onPress={() => {
                    // Ensure URL has protocol
                    let url = shopInfo.website;
                    if (
                      !url.startsWith("http://") &&
                      !url.startsWith("https://")
                    ) {
                      url = "https://" + url;
                    }
                    Linking.openURL(url);
                  }}
                >
                  {shopInfo.website.replace(/^https?:\/\//, "")}
                </Text>
              </Text>
            )}

            {/* Problem Reports Button - Bottom (if all reports are completed) */}
            {(() => {
              const breakdownReports = booking?.breakdownReports || [];
              const hasReports = breakdownReports.length > 0;
              const allCompleted = hasReports &&
                breakdownReports.every(report => report.completed === true);

              if (allCompleted) {
                const totalCount = breakdownReports.length;
                const completedCount = breakdownReports.filter(r => r.completed === true).length;
                const incompleteCount = breakdownReports.filter(r => !r.completed).length;
                const hasBothTypes = completedCount > 0 && incompleteCount > 0;
                
                return (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.problemReportsButton}
                    onPress={() => navigate(`/problem-reports/${bookingId}`)}
                  >
                    <Text style={styles.problemReportsButtonText}>
                      Filed Reports
                      {totalCount > 0 ? (
                        <>
                          {hasBothTypes && <Text> ({totalCount})</Text>}
                          {completedCount > 0 && (
                            <Text style={{ color: '#1F8A5B' }}> ({completedCount})</Text>
                          )}
                          {incompleteCount > 0 && (
                            <Text style={{ color: '#C4453D' }}> ({incompleteCount})</Text>
                          )}
                        </>
                      ) : (
                        <Text style={{ color: '#5C6B68' }}> (0)</Text>
                      )}
                    </Text>
                  </TouchableOpacity>
                );
              }
              return null;
            })()}

            {/* Edit History Section - Always show for authenticated users (createdBy is always present) */}
            {isAuthenticated && booking && (
              <View style={styles.editHistoryContainer}>
                <TouchableOpacity
                  style={styles.editHistoryHeader}
                  onPress={() => setShowLogs(!showLogs)}
                >
                  <Text style={styles.editHistoryHeaderText}>Edit History</Text>
                  <Text style={styles.editHistoryToggleIcon}>{showLogs ? "▼" : "▶"}</Text>
                </TouchableOpacity>
                {showLogs && (
                  <View style={styles.editHistoryContent}>
                    {/* Created By - First entry, always visible */}
                    {(() => {
                      if (!booking?.createdBy) {
                        return (
                          <View style={[styles.editHistoryItem, styles.editHistoryItemCreated]}>
                            <Text style={styles.editHistoryLabel}>Created by: </Text>
                            <Text style={styles.editHistoryName}>Unknown</Text>
                          </View>
                        );
                      }

                      const firstName = booking.createdBy.firstName || "";
                      const lastName = booking.createdBy.lastName || "";
                      const userId = booking.createdBy.userId;
                      const isCustomerCreated = userId === null || userId === undefined;

                      // Format display name: if customer created, show "(customer created) {firstName} {lastName}"
                      const fullName = `${firstName} ${lastName}`.trim() || "Unknown";
                      const displayName = isCustomerCreated
                        ? `(customer created) ${fullName}`
                        : fullName;

                      let dateTimeText = "";
                      try {
                        if (booking.createdBy.timestamp) {
                          const createdDate = booking.createdBy.timestamp.toDate ? booking.createdBy.timestamp.toDate() : (booking.createdBy.timestamp instanceof Date ? booking.createdBy.timestamp : new Date(booking.createdBy.timestamp));
                          const formattedDate = createdDate.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                          const formattedTime = createdDate.toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          });
                          dateTimeText = ` - ${formattedDate} at ${formattedTime}`;
                        } else if (booking.createdAt) {
                          const createdDate = booking.createdAt.toDate ? booking.createdAt.toDate() : (booking.createdAt instanceof Date ? booking.createdAt : new Date(booking.createdAt));
                          const formattedDate = createdDate.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                          const formattedTime = createdDate.toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          });
                          dateTimeText = ` - ${formattedDate} at ${formattedTime}`;
                        }
                      } catch (err) {
                        // Ignore date formatting errors
                      }

                      return (
                        <View style={[styles.editHistoryItem, styles.editHistoryItemCreated]}>
                          <Text style={styles.editHistoryLabel}>Created by: </Text>
                          <Text style={styles.editHistoryName}>{displayName}</Text>
                          {dateTimeText ? (
                            <Text style={styles.editHistoryDate}>{dateTimeText}</Text>
                          ) : null}
                        </View>
                      );
                    })()}

                    {/* Edited By entries */}
                    {booking?.editedBy && Array.isArray(booking.editedBy) && booking.editedBy.length > 0 && (
                      <>
                        {booking.editedBy.map((edit, index) => {
                          const editDate = edit.timestamp?.toDate ? edit.timestamp.toDate() : (edit.timestamp instanceof Date ? edit.timestamp : new Date(edit.timestamp));
                          const formattedDate = editDate.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          });
                          const formattedTime = editDate.toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          });
                          const fullName = `${edit.firstName || ""} ${edit.lastName || ""}`.trim();
                          const isEven = index % 2 === 0;

                          return (
                            <View key={index} style={[
                              styles.editHistoryItem,
                              isEven ? styles.editHistoryItemEven : styles.editHistoryItemOdd
                            ]}>
                              <Text style={styles.editHistoryLabel}>Edited by: </Text>
                              <Text style={styles.editHistoryName}>{fullName || "Unknown"}</Text>
                              <Text style={styles.editHistoryDate}> - {formattedDate} at {formattedTime}</Text>
                            </View>
                          );
                        })}
                      </>
                    )}
                  </View>
                )}
              </View>
            )}

            {/* Footer */}
            <View style={styles.footer}>
              <Text style={styles.footerText}>
                This page updates in real-time. Bookmark this link to check your
                booking status anytime.
              </Text>
            </View>

            {/* Direct Edit Booking - authorized users only */}
            {isAuthenticated && bookingId && (
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.directEditButton}
                onPress={() => navigate(`/direct-edit-booking/${bookingId}`)}
              >
                <Text style={styles.directEditButtonText}>Direct Edit Booking</Text>
              </TouchableOpacity>
            )}

            {/* Database booking ID - authorized users only */}
            {isAuthenticated && bookingId && (
              <Text style={styles.dbBookingIdText}>Booking ID: {bookingId}</Text>
            )}
          </View>
        </ScrollView>

        {/* Legal Footer Links */}
        <View style={{ marginTop: 40, marginBottom: 20, alignItems: 'center', borderTopWidth: 1, borderTopColor: '#EDF1F0', paddingTop: 20 }}>
          <Text style={{ textAlign: 'center', color: '#5C6B68', fontSize: 12 }}>
            <Text style={{ fontWeight: '600' }}>Bonita Bikes</Text>
            {'\n'}
            By booking, you agree to receive transactional text messages.
          </Text>
          <View style={{ flexDirection: 'row', marginTop: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
            <TouchableOpacity onPress={() => navigate('/privacy-policy')}>
              <Text style={{ color: '#0E7C6B', fontSize: 12, marginHorizontal: 10 }}>Privacy Policy</Text>
            </TouchableOpacity>
            <Text style={{ color: '#DDE3E2' }}>|</Text>
            <TouchableOpacity onPress={() => navigate('/terms-conditions')}>
              <Text style={{ color: '#0E7C6B', fontSize: 12, marginHorizontal: 10 }}>Terms & Conditions</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Loading Modal for Waiver Reminder */}
        <Modal
          visible={sendingWaiverReminder}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Sending waiver reminder...</Text>
            </View>
          </View>
        </Modal>

        {/* Report Type Selection Modal */}
        <Modal
          visible={showReportTypeModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowReportTypeModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.reportTypeModalContent}>
              <Text style={styles.reportTypeModalTitle}>
                Report Type
              </Text>
              <Text style={styles.reportTypeModalQuestion}>
                Are you reporting a breakdown that needs mobile assistance, or is this a simple problem report?
              </Text>
              <View style={styles.reportTypeButtonsContainer}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.roadsideBreakdownButton}
                  onPress={handleRoadsideBreakdown}
                  disabled={reportingBreakdown}
                >
                  <Text style={styles.roadsideBreakdownButtonText}>
                    🚨 Roadside Breakdown
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.problemReportButton}
                  onPress={handleProblemReport}
                  disabled={reportingBreakdown}
                >
                  <Text style={styles.problemReportButtonText}>
                    📋 Problem Report
                  </Text>
                </TouchableOpacity>
              </View>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={styles.cancelReportButton}
                onPress={() => setShowReportTypeModal(false)}
              >
                <Text style={styles.cancelReportButtonText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>

        {/* Problem Description Modal */}
        <Modal
          visible={showProblemDescriptionModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            if (!submittingProblemReport) {
              setShowProblemDescriptionModal(false);
            }
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.problemDescriptionModalContent}>
              <ScrollView style={styles.problemDescriptionScrollView}>
                <Text style={styles.problemDescriptionModalTitle}>
                  Report Problem
                </Text>
                <Text style={styles.problemDescriptionModalMessage}>
                  Write a message describing the problem, then you will be directed to the camera page to take pictures, please be thorough!
                </Text>

                {/* Problem Description Input */}
                <TextInput
                  style={styles.problemDescriptionInput}
                  placeholder="Describe the problem (REQUIRED)..."
                  placeholderTextColor="#8A9794"
                  multiline
                  numberOfLines={4}
                  value={problemDescription}
                  onChangeText={setProblemDescription}
                  editable={!submittingProblemReport}
                  autoCapitalize="sentences"
                />

                {/* Location Input (required if GPS is wrong, optional otherwise) */}
                {isRoadsideBreakdown && (
                  <View style={styles.locationInputContainer}>
                    <Text style={styles.locationInputLabel}>
                      Location {isGPSWrong && <Text style={{ color: "#C4453D" }}>*</Text>}
                      {!isGPSWrong && " (Optional)"}
                    </Text>
                    <TextInput
                      style={styles.problemDescriptionInput}
                      placeholder={isGPSWrong
                        ? "Due to faulty coordinates, enter directions or location of the equipment"
                        : "Enter address or location description..."}
                      placeholderTextColor="#8A9794"
                      multiline
                      numberOfLines={4}
                      textAlignVertical="top"
                      value={problemLocation}
                      onChangeText={setProblemLocation}
                      editable={!submittingProblemReport}
                      autoCapitalize="sentences"
                    />
                  </View>
                )}

                {/* GPS Location Link (only if GPS was obtained) */}
                {isRoadsideBreakdown && problemGPS && (
                  <TouchableOpacity dataSet={{ hover: "lift" }}
                    style={styles.gpsLinkContainer}
                    onPress={() => {
                      navigate("/map-viewer", {
                        state: {
                          lat: problemGPS.latitude,
                          lng: problemGPS.longitude,
                          bookingId: bookingId,
                        },
                      });
                    }}
                    disabled={submittingProblemReport}
                  >
                    <Text style={styles.gpsLinkText}>
                      📍 {problemGPSCity || "Loading location..."}
                    </Text>
                    <Text style={styles.gpsLinkSubtext}>
                      Tap to view on map
                    </Text>
                  </TouchableOpacity>
                )}

                {/* Photo Thumbnails */}
                <View style={styles.photosContainer}>
                  <Text style={styles.photosSectionTitle}>
                    Photos ({problemPhotos.length})
                  </Text>
                  {problemPhotos.length > 0 ? (
                    <View style={styles.photoThumbnailsContainer}>
                      {problemPhotos.map((photo, index) => (
                        <View key={index} style={styles.photoThumbnailWrapper}>
                          <Image
                            source={{ uri: typeof photo === 'string' ? photo : photo.uri || photo }}
                            style={styles.photoThumbnail}
                            resizeMode="cover"
                          />
                          <TouchableOpacity dataSet={{ hover: "lift" }}
                            style={styles.deletePhotoButton}
                            onPress={() => deleteProblemPhoto(index)}
                            disabled={submittingProblemReport}
                          >
                            <Text style={styles.deletePhotoButtonText}>✕</Text>
                          </TouchableOpacity>
                        </View>
                      ))}
                    </View>
                  ) : (
                    <Text style={styles.noPhotosText}>
                      At least 1 photo required. Tap "Take Photo" to add photos.
                    </Text>
                  )}
                </View>

                {/* Take Photo Button */}
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.takePhotoButton}
                  onPress={takeProblemPhoto}
                  disabled={submittingProblemReport}
                >
                  <Text style={styles.takePhotoButtonText}>
                    📸 {problemPhotos.length > 0 ? "Take More Photos" : "Take Photo"}
                  </Text>
                </TouchableOpacity>
              </ScrollView>

              {/* Action Buttons */}
              <View style={styles.problemDescriptionModalActions}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.cancelProblemButton}
                  onPress={() => {
                    if (!submittingProblemReport) {
                      setShowProblemDescriptionModal(false);
                      setProblemDescription("");
                      setProblemLocation("");
                      setProblemPhotos([]);
                      setProblemGPS(null);
                      setProblemGPSCity(null);
                      setIsGPSWrong(false);
                    }
                  }}
                  disabled={submittingProblemReport}
                >
                  <Text style={styles.cancelProblemButtonText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={[
                    styles.submitProblemButton,
                    (problemPhotos.length === 0 || !problemDescription.trim() || (isGPSWrong && !problemLocation.trim())) && styles.submitProblemButtonDisabled
                  ]}
                  onPress={handleSubmitProblemReport}
                  disabled={submittingProblemReport || problemPhotos.length === 0 || !problemDescription.trim() || (isGPSWrong && !problemLocation.trim())}
                >
                  {submittingProblemReport ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <Text style={styles.submitProblemButtonText}>Submit</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Blocking Modal During GPS Fetch */}
        <Modal
          visible={fetchingGPS}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Getting your location...</Text>
            </View>
          </View>
        </Modal>

        {/* GPS Confirmation Modal with Map */}
        <Modal
          visible={showGPSConfirmationModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            // Prevent closing by back button - user must choose Correct or Wrong
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.gpsConfirmationModalContent}>
              <Text style={styles.gpsConfirmationModalTitle}>
                Confirm Your Location
              </Text>
              <Text style={styles.gpsConfirmationModalMessage}>
                Please verify that the location shown on the map is correct.
              </Text>

              {/* Map Container */}
              <View style={styles.gpsMapContainer}>
                {!gpsMapLoaded && (
                  <View style={styles.gpsMapLoading}>
                    <ActivityIndicator size="large" color="#0E7C6B" />
                    <Text style={styles.gpsMapLoadingText}>Loading map...</Text>
                  </View>
                )}
                <div
                  ref={gpsMapRef}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "300px",
                    borderRadius: 8,
                  }}
                />
              </View>

              {/* GPS Location Display */}
              {pendingGPS && (
                <View style={styles.gpsCoordinatesDisplay}>
                  <Text style={styles.gpsCoordinatesText}>
                    📍 {pendingGPSCity || "Loading location..."}
                  </Text>
                </View>
              )}

              {/* Action Buttons */}
              <View style={styles.gpsConfirmationButtons}>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.gpsWrongButton}
                  onPress={handleGPSWrong}
                >
                  <Text style={styles.gpsWrongButtonText}>Wrong</Text>
                </TouchableOpacity>
                <TouchableOpacity dataSet={{ hover: "lift" }}
                  style={styles.gpsCorrectButton}
                  onPress={handleGPSCorrect}
                >
                  <Text style={styles.gpsCorrectButtonText}>Correct</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Modal>

        {/* Blocking Modal During Submission */}
        <Modal
          visible={submittingProblemReport}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalContent}>
              <ActivityIndicator size="large" color="#0E7C6B" />
              <Text style={styles.modalText}>Submitting problem report...</Text>
            </View>
          </View>
        </Modal>

        {/* Contact Options Modal */}
        <ContactOptionsModal
          visible={contactModalVisible}
          onClose={() => setContactModalVisible(false)}
          customer={selectedCustomer}
        />
      </View>
    );
  } catch (renderError) {
    console.error("❌ RENDER ERROR:", renderError);
    return (
      <View style={styles.container}>
        <View style={styles.content}>
          <Text style={styles.errorText}>❌ Error displaying booking</Text>
          <Text style={styles.errorSubtext}>{renderError.message}</Text>
          <Text
            style={{
              fontSize: 12,
              color: "#8A9794",
              marginTop: 20,
              textAlign: "center",
            }}
          >
            Please try refreshing the page or contact support.
          </Text>
        </View>
      </View>
    );
  }
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  checkInButton: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  checkInButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  viewWaiversButton: {
    backgroundColor: "#1F8A5B",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  viewWaiversButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  photosVideosContainer: {
    marginTop: 20,
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#E4F4EC",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  photosVideosLabel: {
    fontSize: 14,
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 20,
  },
  photosVideosButton: {
    backgroundColor: "#1F8A5B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  photosVideosButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  photoButtonRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    marginTop: 20,
    marginBottom: 8,
  },
  // Shared by both flow buttons so the inactive one always reads the same way,
  // whichever half of the pair it is. #9FC5BE is the app's muted-green disabled
  // fill (see StepTransportation's validateButtonDisabled).
  flowButtonDisabled: {
    backgroundColor: "#9FC5BE",
    opacity: 0.65,
    cursor: "not-allowed",
  },
  checkOutButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#0E7C6B",
    borderRadius: 6,
  },
  checkOutButtonIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  // Checked-out state: keeps the muted "done" look but stays pressable, so no
  // not-allowed cursor here (flowButtonDisabled is for truly dead buttons).
  checkOutButtonDone: {
    backgroundColor: "#9FC5BE",
    opacity: 0.65,
  },
  checkOutButtonIconDone: {
    color: "#1F8A5B",
    fontWeight: "700",
  },
  checkOutButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  checkInRowButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: "#1F8A5B",
    borderRadius: 6,
  },
  checkInRowButtonIcon: {
    fontSize: 18,
    color: "#FFFFFF",
    fontWeight: "bold",
    marginRight: 8,
  },
  checkInRowButtonText: {
    fontSize: 16,
    color: "#FFFFFF",
    fontWeight: "600",
  },
  adminNotesCard: {
    marginTop: 20,
    marginBottom: 0,
    marginHorizontal: 0,
    padding: 10,
    backgroundColor: "#FDF3E3",
    borderRadius: 5,
    borderWidth: 1,
    borderColor: "#F4F6F6",
    borderLeftWidth: 3,
    borderLeftColor: "#B4761B",
  },
  adminNotesLabel: {
    fontSize: 11,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 4,
  },
  adminNotesText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 18,
  },
  bookingDetailsContainer: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 6,
    marginTop: 20,
    marginBottom: 20,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  bookingDetailsDivider: {
    height: 1,
    backgroundColor: "#DDE3E2",
    marginTop: 12,
    marginBottom: 12,
  },
  reportButtonsContainer: {
    marginTop: 20,
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#FBEAE9",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#C4453D",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  reportButtonsLabel: {
    fontSize: 14,
    color: "#2E3A38",
    textAlign: "center",
    marginBottom: 12,
    lineHeight: 20,
  },
  reportButtonsRow: {
    flexDirection: "row",
    gap: 12,
  },
  reportBreakdownButton: {
    backgroundColor: "#C4453D",
    padding: 16,
    borderRadius: 6,
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  reportBreakdownButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  viewProblemReportsButton: {
    backgroundColor: "#8A9794",
    padding: 16,
    borderRadius: 6,
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  viewProblemReportsButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  breakdownReportedButton: {
    backgroundColor: "#C4453D",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  breakdownReportedButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  problemReportsButton: {
    backgroundColor: "#8A9794",
    padding: 16,
    borderRadius: 6,
    margin: 16,
    marginBottom: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  problemReportsButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F6F8F7",
    minHeight: "100vh",
  },
  loadingContent: {
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
    minWidth: 300,
  },
  loadingLogo: {
    width: 150,
    height: 100,
    marginBottom: 20,
    alignSelf: "center",
  },
  loadingIconContainer: {
    position: "relative",
    width: 100,
    height: 100,
    marginBottom: 24,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingIcon: {
    fontSize: 64,
    position: "absolute",
  },
  loadingTitle: {
    fontSize: 28,
    fontWeight: "700",
    color: "#0E7C6B",
    marginBottom: 8,
    textAlign: "center",
  },
  loadingSubtitle: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 24,
    textAlign: "center",
  },
  loadingBarContainer: {
    width: "100%",
    marginBottom: 16,
  },
  loadingBar: {
    height: 8,
    backgroundColor: "#DDE3E2",
    borderRadius: 4,
    overflow: "hidden",
  },
  loadingBarFill: {
    height: "100%",
    backgroundColor: "#0E7C6B",
    borderRadius: 4,
    width: "75%",
  },
  loadingHint: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  content: {
    padding: 20,
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  logoBanner: {
    backgroundColor: "#0F1A19",
    width: "100%",
    paddingVertical: 30,
    paddingHorizontal: 20,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 0,
    borderRadius: 8,
  },
  bannerLogo: {
    width: 180,
    height: 120,
  },
  logoBannerText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
    textAlign: "center",
    marginTop: 16,
    lineHeight: 26,
  },
  flaggedIndicator: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FDF3E3",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 6,
    marginTop: 20,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: "#B4761B",
  },
  flaggedIcon: {
    fontSize: 20,
    marginRight: 10,
  },
  flaggedText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#7A4E12",
  },
  bannerOrange: {
    backgroundColor: "#B4761B",
    padding: 16,
    borderRadius: 6,
    marginTop: 20,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 4,
  },
  bannerRed: {
    backgroundColor: "#C4453D",
    padding: 16,
    borderRadius: 0,
    marginTop: 20,
    marginBottom: 16,
    borderLeftWidth: 6,
    borderLeftColor: "#C4453D",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderTopColor: "#C4453D",
    borderBottomColor: "#C4453D",
    borderRightColor: "#C4453D",
  },
  bannerGreen: {
    backgroundColor: "#1F8A5B",
    padding: 16,
    borderRadius: 0,
    marginTop: 20,
    marginBottom: 16,
    borderLeftWidth: 6,
    borderLeftColor: "#1F8A5B",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderRightWidth: 1,
    borderTopColor: "#1F8A5B",
    borderBottomColor: "#1F8A5B",
    borderRightColor: "#1F8A5B",
  },
  bannerText: {
    fontSize: 16,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
    lineHeight: 22,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    marginTop: 10,
    marginBottom: 20,
    color: "#2E3A38",
  },
  lockAssignmentContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 30,
    marginTop: 10,
  },
  lockAssignmentButton: {
    width: 50,
    height: 50,
    borderRadius: 25,
    borderWidth: 2,
    borderColor: "#0E7C6B",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 16,
    backgroundColor: "transparent",
  },
  lockAssignmentButtonText: {
    fontSize: 24,
    textAlign: "center",
  },
  lockListContainer: {
    flex: 1,
    justifyContent: "center",
  },
  lockListItem: {
    paddingVertical: 4,
  },
  lockListText: {
    fontSize: 15,
    color: "#2E3A38",
    fontWeight: "500",
  },
  noLocksText: {
    fontSize: 15,
    color: "#8A9794",
    fontStyle: "italic",
  },
  refundSection: {
    backgroundColor: "#FDF3E3",
    padding: 16,
    borderRadius: 6,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  refundSectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#B4761B",
    marginBottom: 12,
  },
  refundItem: {
    backgroundColor: "#FFFFFF",
    padding: 12,
    borderRadius: 5,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#B4761B",
  },
  refundText: {
    fontSize: 14,
    color: "#2E3A38",
    marginBottom: 4,
  },
  refundId: {
    fontSize: 12,
    color: "#5C6B68",
    fontFamily: "monospace",
    marginTop: 4,
  },
  refundTotal: {
    backgroundColor: "#1F8A5B",
    padding: 12,
    borderRadius: 5,
    marginTop: 10,
    alignItems: "center",
  },
  refundTotalText: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  editButton: {
    backgroundColor: "#C4453D",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 16,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  editButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
  },
  refundNotice: {
    backgroundColor: "#FDF3E3",
    padding: 12,
    borderRadius: 6,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  refundNoticeText: {
    fontSize: 13,
    color: "#5C6B68",
    lineHeight: 18,
    textAlign: "center",
  },
  linkText: {
    color: "#0E7C6B",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  cancelButton: {
    backgroundColor: "#C4453D",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 10,
    marginBottom: 15,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  cancelButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  reactivateButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    marginTop: 10,
    marginBottom: 15,
    alignItems: "center",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  reactivateButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  completedBookingContainer: {
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderRadius: 6,
    padding: 16,
    marginTop: 20,
    marginBottom: 16,
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  transferSection: {
    backgroundColor: "#E3F2EF",
    padding: 16,
    borderRadius: 6,
    marginTop: 20,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  transferTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  transferDescription: {
    fontSize: 13,
    color: "#5C6B68",
    marginBottom: 12,
    lineHeight: 18,
  },
  transferInput: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 5,
    padding: 12,
    fontSize: 15,
    marginBottom: 12,
  },
  transferButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
  },
  transferButtonText: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    textAlign: "center",
  },
  cancellationPolicyContainer: {
    backgroundColor: "#FDF3E3",
    padding: 16,
    borderRadius: 6,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  cancellationPolicyTitle: {
    fontSize: 15,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  cancellationPolicyText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 18,
  },
  section: {
    backgroundColor: "#FFFFFF",
    padding: 16,
    borderRadius: 6,
    marginBottom: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
    color: "#0E7C6B",
    paddingBottom: 8,
  },
  customerSection: {
    backgroundColor: "#F4F6F6",
    borderLeftColor: "#5C6B68",
  },
  rentalPeriodSection: {
    backgroundColor: "#FDF3E3",
    borderLeftColor: "#B4761B",
  },
  product1Section: {
    backgroundColor: "#E3F2EF",
    borderLeftColor: "#0E7C6B",
  },
  product2Section: {
    backgroundColor: "#FDF3E3",
    borderLeftColor: "#B4761B",
  },
  pricingSection: {
    backgroundColor: "#E4F4EC",
    borderLeftColor: "#1F8A5B",
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
    flexWrap: "wrap",
  },
  label: {
    fontSize: 14,
    color: "#5C6B68",
    fontWeight: "500",
    marginRight: 8,
  },
  value: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
    textAlign: "right",
  },
  addressContainer: {
    flex: 1,
    alignItems: "flex-end",
  },
  addressLinkText: {
    fontSize: 15,
    color: "#0E7C6B",
    fontWeight: "600",
    textAlign: "right",
  },
  priceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  priceLabel: {
    fontSize: 14,
    color: "#5C6B68",
  },
  priceValue: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "500",
  },
  discount: {
    color: "#1F8A5B",
  },
  totalRow: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 2,
    borderTopColor: "#0E7C6B",
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  totalValue: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0E7C6B",
  },
  actionButtonsContainer: {
    width: "100%",
    marginTop: 16,
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#F4F6F6",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  paymentButtonsContainer: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  collectButton: {
    flex: 1,
    backgroundColor: "#C4453D", // Red - same as Bookings page
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  collectButtonDisabled: {
    opacity: 0.6,
  },
  collectButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  sendLinkButton: {
    flex: 1,
    backgroundColor: "#0E7C6B", // Magenta - same as Bookings page
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  sendLinkButtonDisabled: {
    opacity: 0.6,
  },
  sendLinkButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  payInvoiceButton: {
    backgroundColor: "#1F8A5B", // Green
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  payInvoiceButtonDisabled: {
    opacity: 0.6,
  },
  payInvoiceButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  waiverActionsContainer: {
    marginTop: 16,
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  signWaiverButton: {
    backgroundColor: "#B4761B", // Standard yellow
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  waiverRequiredText: {
    fontSize: 13,
    color: "#C4453D",
    fontWeight: "600",
    textAlign: "center",
    marginBottom: 8,
  },
  signWaiverButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  sendWaiverReminderButton: {
    backgroundColor: "#B4761B", // Slightly darker yellow
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 12,
  },
  sendWaiverReminderButtonText: {
    color: "#2E3A38",
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
  },
  blurbContainer: {
    marginTop: 20,
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 2,
  },
  blurbText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
    textAlign: "center",
  },
  contactInstructionsContainer: {
    marginTop: 16,
    padding: 16,
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  contactInstructionsTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
    textAlign: "center",
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
    paddingVertical: 8,
  },
  contactIcon: {
    fontSize: 18,
    marginRight: 12,
  },
  contactText: {
    fontSize: 14,
    color: "#2E3A38",
    flex: 1,
  },
  contactLink: {
    color: "#0E7C6B",
    textDecorationLine: "underline",
  },
  storeHoursContainer: {
    backgroundColor: "#F4F6F6",
    borderColor: "#5C6B68",
    borderWidth: 1,
  },
  hoursTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#0E7C6B",
    marginBottom: 16,
    textAlign: "center",
  },
  hoursList: {
    // gap not supported in React Native - using marginBottom on children instead
  },
  hoursRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "rgba(255, 255, 255, 0.5)",
    borderRadius: 5,
    marginBottom: 8,
  },
  dayName: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
    minWidth: 100,
  },
  hoursTime: {
    fontSize: 15,
    color: "#5C6B68",
  },
  websiteContainer: {
    marginTop: 24,
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
  },
  websiteText: {
    fontSize: 14,
    color: "#2E3A38",
    textAlign: "center",
    lineHeight: 20,
    marginTop: 24,
    marginBottom: 16,
  },
  websiteLink: {
    color: "#0E7C6B",
    fontWeight: "600",
    textDecorationLine: "underline",
  },
  createdByContainer: {
    marginTop: 20,
    marginBottom: 10,
    padding: 12,
    backgroundColor: "#F6F8F7",
    borderRadius: 6,
    alignItems: "center",
  },
  createdByText: {
    fontSize: 12,
    color: "#5C6B68",
    textAlign: "center",
    fontStyle: "italic",
  },
  editedByContainer: {
    marginTop: 10,
    marginBottom: 10,
    padding: 12,
    backgroundColor: "#F6F8F7",
    borderRadius: 6,
  },
  editedByTitle: {
    fontSize: 13,
    color: "#2E3A38",
    fontWeight: "600",
    marginBottom: 8,
    textAlign: "center",
  },
  editedByItem: {
    marginBottom: 6,
  },
  editedByText: {
    fontSize: 11,
    color: "#5C6B68",
    textAlign: "center",
    fontStyle: "italic",
  },
  editHistoryContainer: {
    marginTop: 20,
    marginBottom: 10,
    marginHorizontal: 16,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
  },
  editHistoryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#F6F8F7",
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  editHistoryHeaderText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  editHistoryToggleIcon: {
    fontSize: 14,
    color: "#5C6B68",
  },
  editHistoryContent: {
    padding: 16,
  },
  editHistoryItem: {
    marginBottom: 8,
    padding: 12,
    borderRadius: 5,
    borderLeftWidth: 4,
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
  },
  editHistoryItemCreated: {
    backgroundColor: "#E4F4EC",
    borderLeftColor: "#1F8A5B",
  },
  editHistoryItemEven: {
    backgroundColor: "#E3F2EF",
    borderLeftColor: "#0E7C6B",
  },
  editHistoryItemOdd: {
    backgroundColor: "#FDF3E3",
    borderLeftColor: "#B4761B",
  },
  editHistoryLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#5C6B68",
    marginRight: 4,
  },
  editHistoryName: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginRight: 4,
  },
  editHistoryDate: {
    fontSize: 13,
    color: "#8A9794",
    fontStyle: "italic",
  },
  editHistoryText: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
  },
  viewLogsContainer: {
    marginTop: 20,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  viewLogsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    backgroundColor: "#F6F8F7",
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  viewLogsHeaderText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  viewLogsToggleIcon: {
    fontSize: 14,
    color: "#5C6B68",
  },
  viewLogsContent: {
    maxHeight: 400,
  },
  logItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
    backgroundColor: "#FFFFFF",
  },
  logItemHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  logItemUser: {
    fontSize: 15,
    fontWeight: "600",
    color: "#2E3A38",
  },
  logItemDate: {
    fontSize: 12,
    color: "#5C6B68",
  },
  logItemDescription: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 8,
    lineHeight: 20,
  },
  logItemMeta: {
    flexDirection: "row",
    gap: 16,
    flexWrap: "wrap",
  },
  logItemType: {
    fontSize: 12,
    color: "#0E7C6B",
    fontWeight: "500",
  },
  logItemField: {
    fontSize: 12,
    color: "#8A9794",
  },
  footer: {
    marginTop: 30,
    marginBottom: 20,
    padding: 16,
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
  },
  footerText: {
    fontSize: 12,
    color: "#5C6B68",
    textAlign: "center",
    lineHeight: 18,
  },
  directEditButton: {
    alignSelf: "center",
    marginTop: 16,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#DDE3E2",
    borderRadius: 6,
  },
  directEditButtonText: {
    fontSize: 14,
    color: "#2E3A38",
    fontWeight: "500",
  },
  dbBookingIdText: {
    fontSize: 10,
    color: "#8A9794",
    textAlign: "center",
    marginTop: 12,
    marginBottom: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  errorText: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#C4453D",
    marginBottom: 8,
    textAlign: "center",
  },
  errorSubtext: {
    fontSize: 14,
    color: "#5C6B68",
    textAlign: "center",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    padding: 30,
    borderRadius: 8,
    alignItems: "center",
    minWidth: 200,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  modalText: {
    marginTop: 16,
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  reportTypeModalContent: {
    backgroundColor: "#FFFFFF",
    padding: 30,
    borderRadius: 12,
    alignItems: "center",
    minWidth: 320,
    maxWidth: 500,
    width: "90%",
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  reportTypeModalTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 16,
    textAlign: "center",
  },
  reportTypeModalQuestion: {
    fontSize: 16,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 22,
  },
  reportTypeButtonsContainer: {
    width: "100%",
    gap: 12,
    marginBottom: 16,
  },
  roadsideBreakdownButton: {
    backgroundColor: "#C4453D",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  roadsideBreakdownButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  problemReportButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  problemReportButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
  },
  cancelReportButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    marginTop: 8,
  },
  cancelReportButtonText: {
    color: "#5C6B68",
    fontSize: 16,
    textAlign: "center",
  },
  // Problem Description Modal Styles
  problemDescriptionModalContent: {
    backgroundColor: "#FFFFFF",
    padding: 24,
    borderRadius: 12,
    minWidth: 320,
    maxWidth: 500,
    width: "90%",
    maxHeight: "85%",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
  },
  problemDescriptionScrollView: {
    maxHeight: "70vh",
  },
  problemDescriptionModalTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
    textAlign: "center",
  },
  problemDescriptionModalMessage: {
    fontSize: 15,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 22,
  },
  problemDescriptionContainer: {
    marginBottom: 16,
  },
  problemDescriptionLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  problemDescriptionInput: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 100,
    textAlignVertical: "top",
    backgroundColor: "#FFFFFF",
    width: "100%",
  },
  locationInputContainer: {
    marginBottom: 16,
  },
  locationInputLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  gpsLinkContainer: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#0E7C6B",
    alignItems: "center",
  },
  gpsLinkText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "600",
    marginBottom: 4,
  },
  gpsLinkSubtext: {
    fontSize: 12,
    color: "#5C6B68",
  },
  photosContainer: {
    marginBottom: 16,
  },
  photosSectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 12,
  },
  photoThumbnailsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  photoThumbnailWrapper: {
    position: "relative",
    width: 80,
    height: 80,
    borderRadius: 6,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  photoThumbnail: {
    width: "100%",
    height: "100%",
  },
  deletePhotoButton: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(255, 59, 48, 0.9)",
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  deletePhotoButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "bold",
  },
  noPhotosText: {
    fontSize: 14,
    color: "#8A9794",
    fontStyle: "italic",
    textAlign: "center",
    paddingVertical: 20,
  },
  takePhotoButton: {
    backgroundColor: "#0E7C6B",
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  takePhotoButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  problemDescriptionModalActions: {
    flexDirection: "row",
    gap: 12,
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#EDF1F0",
  },
  cancelProblemButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EDF1F0",
  },
  cancelProblemButtonText: {
    color: "#5C6B68",
    fontSize: 16,
    fontWeight: "600",
  },
  submitProblemButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1F8A5B",
  },
  submitProblemButtonDisabled: {
    backgroundColor: "#DDE3E2",
    opacity: 0.6,
  },
  submitProblemButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  // GPS Confirmation Modal Styles
  gpsConfirmationModalContent: {
    backgroundColor: "#FFFFFF",
    padding: 24,
    borderRadius: 12,
    minWidth: 320,
    maxWidth: 600,
    width: "90%",
    maxHeight: "85%",
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 5,
  },
  gpsConfirmationModalTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
    textAlign: "center",
  },
  gpsConfirmationModalMessage: {
    fontSize: 15,
    color: "#5C6B68",
    textAlign: "center",
    marginBottom: 20,
    lineHeight: 22,
  },
  gpsMapContainer: {
    width: "100%",
    height: 300,
    borderRadius: 8,
    overflow: "hidden",
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    backgroundColor: "#EDF1F0",
  },
  gpsMapLoading: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#EDF1F0",
    zIndex: 1,
  },
  gpsMapLoadingText: {
    marginTop: 12,
    fontSize: 14,
    color: "#5C6B68",
  },
  gpsCoordinatesDisplay: {
    backgroundColor: "#E3F2EF",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    alignItems: "center",
  },
  gpsCoordinatesText: {
    fontSize: 14,
    color: "#0E7C6B",
    fontWeight: "600",
  },
  gpsConfirmationButtons: {
    flexDirection: "row",
    gap: 12,
  },
  gpsWrongButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#C4453D",
  },
  gpsWrongButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  gpsCorrectButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#1F8A5B",
  },
  gpsCorrectButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  infoIcon: {
    padding: 4,
    borderRadius: 8,
    minWidth: 24,
    minHeight: 24,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 7,
  },
  infoIconText: {
    fontSize: 14,
  },
  tooltip: {
    position: "absolute",
    top: 30,
    left: 0,
    right: 0,
    backgroundColor: "#0F1A19",
    padding: 12,
    borderRadius: 6,
    zIndex: 10002,
    elevation: 10002,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    opacity: 1,
  },
  tooltipOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10001,
    elevation: 10001,
  },
  tooltipText: {
    color: "#FFFFFF",
    fontSize: 14,
    lineHeight: 20,
  },
  tooltipArrow: {
    position: "absolute",
    top: -6,
    left: 20,
    width: 0,
    height: 0,
    borderLeftWidth: 6,
    borderRightWidth: 6,
    borderBottomWidth: 6,
    borderLeftColor: "transparent",
    borderRightColor: "transparent",
    borderBottomColor: "#0F1A19",
  },
});

export default ViewBooking;

